import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Calendar, User, MapPin, Hash, Euro, Printer, AlertCircle, Database as DatabaseIcon, Users, Eye, EyeOff, Scissors, ChevronUp, ChevronDown, Plus, Trash2, RotateCcw } from 'lucide-react';
import { format, parseISO, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import { fetchTariffOverridesBatch, resolvePriceForMonth, type TariffOverride } from '../utils/tariffOverrides';
import type { Database } from '../types/supabase';
import { toast } from 'react-hot-toast';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

type Participant = Database['public']['Tables']['participants']['Row'];

export const removeGroupePrefix = (name: string): string => {
  if (!name) return name;
  return name.replace(/^Groupe\s+/i, '');
};

export const normalizeFileName = (name: string): string => {
  return name
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
};

async function captureGroupedPDF(
  invoiceElement: HTMLElement,
  onProgress?: (percent: number) => void,
  useGroupedPagination: boolean = true
): Promise<jsPDF> {
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 5;
  const usableWidth = pageWidth - (margin * 2);
  const usableHeight = pageHeight - (margin * 2);

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true
  });

  const addCanvasToPage = (canvas: HTMLCanvasElement, isFirst: boolean) => {
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const widthRatio = usableWidth / imgWidth;
    const heightRatio = usableHeight / imgHeight;
    const fitRatio = Math.min(widthRatio, heightRatio);
    const finalWidth = imgWidth * fitRatio;
    const finalHeight = imgHeight * fitRatio;
    const x = margin + (usableWidth - finalWidth) / 2;
    const y = margin;

    if (!isFirst) pdf.addPage();

    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    pdf.addImage(imgData, 'JPEG', x, y, finalWidth, finalHeight);
  };

  // Add cover page with logo
  const addCoverPage = async () => {
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');

    // Top accent bar
    pdf.setFillColor(245, 158, 11);
    pdf.rect(0, 0, pageWidth, 8, 'F');

    // Logo
    try {
      const logoImg = new Image();
      logoImg.src = '/images/Logosite2.png';
      await new Promise<void>((resolve, reject) => {
        logoImg.onload = () => resolve();
        logoImg.onerror = () => reject(new Error('logo load failed'));
      });
      const logoCanvas = document.createElement('canvas');
      logoCanvas.width = logoImg.naturalWidth;
      logoCanvas.height = logoImg.naturalHeight;
      const ctx = logoCanvas.getContext('2d');
      if (ctx) ctx.drawImage(logoImg, 0, 0);
      const logoData = logoCanvas.toDataURL('image/png');
      const logoW = 80;
      const logoH = (logoImg.naturalHeight / logoImg.naturalWidth) * logoW;
      pdf.addImage(logoData, 'PNG', (pageWidth - logoW) / 2, 60, logoW, logoH);
    } catch {
      // Fallback: text logo
      pdf.setFontSize(28);
      pdf.setTextColor(245, 158, 11);
      pdf.text('Sun Is Up', pageWidth / 2, 90, { align: 'center' });
    }

    pdf.setFontSize(22);
    pdf.setTextColor(30, 30, 30);
    pdf.text('Sun Is Up ASBL', pageWidth / 2, 160, { align: 'center' });

    pdf.setFontSize(13);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Communauté d\'énergie bruxelloise', pageWidth / 2, 170, { align: 'center' });
    pdf.text('TVA: BE 1022 108 004', pageWidth / 2, 178, { align: 'center' });
    pdf.text('info@sunisup.be', pageWidth / 2, 186, { align: 'center' });

    pdf.setDrawColor(245, 158, 11);
    pdf.setLineWidth(0.5);
    pdf.line(40, 200, pageWidth - 40, 200);

    pdf.setFontSize(16);
    pdf.setTextColor(30, 30, 30);
    pdf.text('Facture énergétique', pageWidth / 2, 220, { align: 'center' });

    // Bottom accent bar
    pdf.setFillColor(245, 158, 11);
    pdf.rect(0, pageHeight - 8, pageWidth, 8, 'F');
  };

  if (!useGroupedPagination) {
    const canvas = await html2canvas(invoiceElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: 1200,
      windowHeight: invoiceElement.scrollHeight,
      height: invoiceElement.scrollHeight
    });
    addCanvasToPage(canvas, true);
    if (onProgress) onProgress(80);
    return pdf;
  }

  // Add cover page first
  await addCoverPage();
  if (onProgress) onProgress(30);

  const directChildren = Array.from(invoiceElement.children).filter(
    (child) => !child.classList.contains('no-print')
  ) as HTMLElement[];

  // Page 0 = cover (added separately), then:
  //   Page 1: header + billed-to + energy detail
  //   Page 2: network costs + excise + financial summary + payment terms
  //   Page 3: annex network costs per participant (consumers/prosumers only)
  const pageSections = [
    ['invoice-subject', 'participant-info', 'energy-detail'],
    ['network-costs', 'excise-costs', 'financial-summary', 'payment-terms'],
    ['annex-network-costs'],
  ];

  for (let pageIdx = 0; pageIdx < pageSections.length; pageIdx++) {
    const sectionNames = pageSections[pageIdx];
    const groupChildren = directChildren.filter((child) => {
      const section = child.getAttribute('data-section');
      if (pageIdx === 0) {
        return !section || sectionNames.includes(section);
      }
      return section != null && sectionNames.includes(section);
    });

    if (groupChildren.length === 0) continue;

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position: fixed; left: -9999px; top: 0; width: 1200px; background: #ffffff; padding: 32px;';
    groupChildren.forEach((child) => {
      wrapper.appendChild(child.cloneNode(true));
    });
    document.body.appendChild(wrapper);

    try {
      const canvas = await html2canvas(wrapper, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1200,
      });
      addCanvasToPage(canvas, false);
    } finally {
      document.body.removeChild(wrapper);
    }

    if (onProgress) {
      onProgress(35 + Math.round(((pageIdx + 1) / pageSections.length) * 45));
    }
  }

  return pdf;
}

/**
 * Numéro figé et mode de paiement d'une facture émise depuis la todolist du CRM (table billing_invoices).
 * Domiciliation : le bloc « Conditions de paiement » affiche la date de prélèvement, l'IBAN masqué,
 * la référence du mandat et l'identifiant créancier au lieu du virement.
 */
export interface InvoiceBillingInfo {
  invoiceNumber: string;
  channel: 'peppol' | 'email' | 'direct_debit';
  /** Date de prélèvement (AAAA-MM-JJ), domiciliation uniquement. */
  collectionDate?: string | null;
  maskedIban?: string | null;
  mandateReference?: string | null;
  creditorId?: string | null;
}

interface InvoiceTemplateProps {
  isOpen: boolean;
  onClose: () => void;
  participant: Participant;
  selectedPeriod: {
    startMonth: string;
    endMonth: string;
  };
  includeMembershipFee?: boolean;
  isAnnualRegularization?: boolean;
  isAdminOnly?: boolean;
  companyNumberFilter?: string | null;
  issueDate?: string | null;
  /**
   * Facture émise depuis le CRM : numéro et paiement fournis (génération du PDF à envoyer).
   * Non fourni : recherche de la facture émise (fonction billing_invoice_info) ; null : aucune recherche.
   */
  billingInfo?: InvoiceBillingInfo | null;
}

export interface GroupCompany {
  company_number: string | null;
  company_name: string | null;
  participants: Participant[];
}

interface MonthlyData {
  volume_partage: number;
  volume_complementaire: number;
  injection_partagee: number;
  injection_complementaire: number;
  updated_at: string;
}

interface NetworkCosts {
  utilisationReseau: number;
  surcharges: number;
  tarifCapacitaire: number;
  tarifMesure: number;
  tarifOSP: number;
  transportELIA: number;
  redevanceVoirie: number;
  totalFraisReseau: number;
}

interface BillingData {
  networkCosts: NetworkCosts;
  updated_at: string;
}

interface ExciseCosts {
  droitAccise: number;
  cotisationEnergie: number;
  totalExcise: number;
  totalExciseTVAC: number;
}

interface InvoiceData {
  participant: Participant;
  period: {
    startMonth: string;
    endMonth: string;
    startDate: string;
    endDate: string;
  };
  monthlyData: { [month: string]: MonthlyData };
  billingData: { [month: string]: BillingData };
  totals: {
    volume_partage: number;
    injection_partagee: number;
    injection_complementaire: number;
    networkCosts: NetworkCosts;
  };
  calculations: {
    energySharedCostHTVA: number;
    energySharedCostTVAC: number;
    networkCostTotal: number;
    networkCostTVAC: number;
    exciseCosts: ExciseCosts;
    membershipFeeHTVA?: number;
    membershipFeeTVAC?: number;
    isFirstInvoiceOfYear?: boolean;
    membershipFeeYear?: number | null;
    totalCostTVAC: number;
    injectionRevenue: number;
    injectionVatRate?: number;
    netAmount: number;
    vatRate: number;
    averageBuyPrice?: number | null;
    averageSellPrice?: number | null;
  };
}

export async function getGroupCompanies(groupe: string): Promise<GroupCompany[]> {
  const { data, error } = await supabase
    .from('participants')
    .select('*')
    .eq('groupe', groupe);

  if (error || !data || data.length === 0) return [];

  const companyMap = new Map<string, GroupCompany>();
  for (const p of data) {
    const key = (p as any).company_number || '__none__';
    if (!companyMap.has(key)) {
      companyMap.set(key, {
        company_number: (p as any).company_number || null,
        company_name: (p as any).company_name || null,
        participants: []
      });
    }
    companyMap.get(key)!.participants.push(p as Participant);
  }
  return Array.from(companyMap.values());
}

export function InvoiceTemplate({ isOpen, onClose, participant, selectedPeriod, includeMembershipFee, isAnnualRegularization: initialIsAnnualRegularization, isAdminOnly = true, companyNumberFilter, issueDate, billingInfo }: InvoiceTemplateProps) {

  const effectiveIssueDate = issueDate ? parseISO(issueDate) : new Date();
  const shouldShowIssueDate = isAdminOnly || !!issueDate;
  const [invoiceData, setInvoiceData] = useState<InvoiceData | null>(null);
  const [groupParticipants, setGroupParticipants] = useState<any[]>([]);
  const [isGroupInvoice, setIsGroupInvoice] = useState(false);
  const [participantNetworkDetails, setParticipantNetworkDetails] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState<string>('');
  const [logoBase64, setLogoBase64] = useState<string>('');
  const [previewMode, setPreviewMode] = useState(false);
  const [pageBreaks, setPageBreaks] = useState<{ position: number; pageNumber: number; reason: string }[]>([]);
  const [previewScale, setPreviewScale] = useState(0.4);
  const [isAnnualRegularization, setIsAnnualRegularization] = useState(initialIsAnnualRegularization ?? false);
  const [billingConfig, setBillingConfig] = useState<Record<string, number>>({
    tva_entreprise: 0.21,
    tva_particulier: 0.06,
    cotisation_energie: 1.9261,
    accise_ent_t1: 14.21,
    accise_ent_t2: 12.09,
    accise_ent_t3: 11.39,
    accise_ent_t4: 10.69,
    accise_part_t1: 47.48,
    accise_part_t2: 45.46,
    accise_part_t3: 44.78,
    accise_part_t4: 44.11,
    adhesion_tvac: 60.50,
  });

  // Load billing config from database
  useEffect(() => {
    if (!isOpen) return;
    supabase
      .from('billing_config')
      .select('key, value')
      .then(({ data }) => {
        if (data && data.length > 0) {
          const cfg: Record<string, number> = {};
          for (const row of data) cfg[row.key] = Number(row.value);
          setBillingConfig(prev => ({ ...prev, ...cfg }));
        }
      });

  }, [isOpen, participant]);

  // Convert logo to base64 for PDF compatibility
  useEffect(() => {
    const convertLogoToBase64 = async () => {
      try {
        const response = await fetch('/images/Logosite2.png');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = () => {
          setLogoBase64(reader.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.warn('Could not load logo for PDF:', error);
        // Fallback: create a simple SVG logo as base64
        const svgLogo = `<svg width="80" height="80" xmlns="http://www.w3.org/2000/svg">
          <circle cx="40" cy="40" r="35" fill="#F59E0B"/>
          <text x="40" y="48" text-anchor="middle" fill="white" font-family="Arial" font-size="24" font-weight="bold">SIU</text>
        </svg>`;
        const base64Svg = `data:image/svg+xml;base64,${btoa(svgLogo)}`;
        setLogoBase64(base64Svg);
      }
    };
    
    if (isOpen) {
      convertLogoToBase64();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && participant) {
      setIsAnnualRegularization(initialIsAnnualRegularization ?? false);
      loadInvoiceData();
    }
  }, [isOpen, participant, selectedPeriod, initialIsAnnualRegularization]);

  useEffect(() => {
    if (isOpen && participant) {
      loadInvoiceData();
    }
  }, [isAnnualRegularization]);

  // Facture émise depuis le CRM : numéro figé et mode de paiement (affichage uniquement, le calcul ne change pas).
  const [storedBilling, setStoredBilling] = useState<InvoiceBillingInfo | null>(null);
  const [billingLookupDone, setBillingLookupDone] = useState(false);
  useEffect(() => {
    if (!isOpen || !participant?.id || billingInfo !== undefined) { setBillingLookupDone(true); return; }
    let cancelled = false;
    setBillingLookupDone(false);
    supabase.rpc('billing_invoice_info', {
      p_participant_id: participant.id,
      p_period_start: selectedPeriod.startMonth,
      p_period_end: selectedPeriod.endMonth,
      p_company_number: companyNumberFilter ?? null,
    }).then(({ data, error }: { data: unknown; error: unknown }) => {
      if (cancelled) return;
      const row = !error && Array.isArray(data) ? (data[0] as Record<string, string | null> | undefined) : undefined;
      setStoredBilling(row?.invoice_number ? {
        invoiceNumber: row.invoice_number,
        channel: (row.channel ?? 'email') as InvoiceBillingInfo['channel'],
        collectionDate: row.collection_date ?? null,
        maskedIban: row.masked_iban ?? null,
        mandateReference: row.mandate_reference ?? null,
        creditorId: row.creditor_id ?? null,
      } : null);
      setBillingLookupDone(true);
    }, () => { if (!cancelled) setBillingLookupDone(true); });
    return () => { cancelled = true; };
  }, [isOpen, participant?.id, selectedPeriod.startMonth, selectedPeriod.endMonth, companyNumberFilter, billingInfo]);
  const billing = billingInfo !== undefined ? billingInfo : storedBilling;

  const processIndividualMonthlyData = (participant: any, months: string[]) => {
    if (!participant.monthly_data) {
      return [];
    }

    let monthlyData;
    try {
      if (typeof participant.monthly_data === 'string') {
        monthlyData = JSON.parse(participant.monthly_data);
      } else {
        monthlyData = participant.monthly_data;
      }
    } catch (error) {
      console.warn('⚠️ Erreur parsing monthly_data:', error);
      return [];
    }

    const monthlyDataArray = Object.entries(monthlyData)
      .filter(([month]) => months.includes(month))
      .map(([month, data]: [string, any]) => ({
        month: format(new Date(month + '-01'), 'MMM yyyy', { locale: fr }),
        monthKey: month,
        volume_partage: Number(data.volume_partage || 0),
        volume_complementaire: Number(data.volume_complementaire || 0),
        injection_partagee: Number(data.injection_partagee || 0),
        injection_complementaire: Number(data.injection_complementaire || 0),
      }))
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey));

    return monthlyDataArray;
  };

  const loadInvoiceData = async () => {
    try {
      setLoading(true);
      setError(null);
      setGroupParticipants([]);

      console.log('🧾 DÉBUT GÉNÉRATION FACTURE');
      console.log('👤 Participant:', participant.name, participant.ean_code);
      console.log('📅 Période:', selectedPeriod);
      console.log('👥 Groupe participant:', participant.groupe);
      
      let allParticipants = [participant];
      let isGroup = false;

      // Si le participant fait partie d'un groupe, charger tous les participants du groupe
      if (participant.groupe) {
        console.log(`👥 Participant fait partie du groupe: "${participant.groupe}"`);
        
        const { data: groupData, error: groupError } = await supabase
          .from('participants')
          .select('*')
          .eq('groupe', participant.groupe);

        if (groupError) {
          console.error('Erreur chargement groupe:', groupError);
          throw new Error(`Impossible de charger les participants du groupe: ${groupError.message}`);
        }

        if (groupData && groupData.length > 1) {
          let filteredGroupData = groupData;
          if (companyNumberFilter !== undefined && companyNumberFilter !== null) {
            filteredGroupData = groupData.filter(p => ((p as any).company_number || null) === companyNumberFilter);
          } else if (companyNumberFilter === null) {
            filteredGroupData = groupData.filter(p => !(p as any).company_number);
          }
          allParticipants = filteredGroupData.length > 0 ? filteredGroupData : groupData;
          isGroup = filteredGroupData.length > 1 || (companyNumberFilter !== undefined && filteredGroupData.length >= 1);
          setGroupParticipants(filteredGroupData.length > 0 ? filteredGroupData : groupData);
          setIsGroupInvoice(isGroup);
          console.log(`✅ ${filteredGroupData.length} participants trouvés dans le groupe "${participant.groupe}"`);
        } else {
          console.log('ℹ️ Participant individuel ou groupe avec un seul membre');
          setIsGroupInvoice(false);
        }
      } else {
        console.log('ℹ️ Participant sans groupe');
        setIsGroupInvoice(false);
      }

      // Récupérer les données du participant depuis la base
      const { data: participantData, error: participantError } = await supabase
        .from('participants')
        .select('*')
        .eq('id', participant.id)
        .single();

      if (participantError) {
        console.error('❌ Erreur chargement participant:', participantError);
        throw new Error('Impossible de charger les données du participant');
      }

      console.log('✅ Données participant chargées');
      console.log('📊 monthly_data présent:', !!participantData.monthly_data);
      console.log('💰 billing_data présent:', !!participantData.billing_data);

      // Debug des données brutes
      const debugData = {
        participantName: participantData.name,
        hasMonthlyData: !!participantData.monthly_data,
        hasBillingData: !!participantData.billing_data,
        monthlyDataRaw: participantData.monthly_data,
        billingDataRaw: participantData.billing_data,
        monthlyDataType: typeof participantData.monthly_data,
        billingDataType: typeof participantData.billing_data
      };
      setDebugInfo(debugData);

      // Parser les données mensuelles
      let monthlyData: { [month: string]: MonthlyData } = {};
      if (participantData.monthly_data) {
        try {
          if (typeof participantData.monthly_data === 'string') {
            monthlyData = JSON.parse(participantData.monthly_data);
          } else {
            monthlyData = participantData.monthly_data;
          }
          console.log('✅ monthly_data parsé:', Object.keys(monthlyData));
        } catch (error) {
          console.warn('⚠️ Erreur parsing monthly_data:', error);
          monthlyData = {};
        }
      }

      // Parser les données de facturation
      let billingData: { [month: string]: BillingData } = {};
      if (participantData.billing_data) {
        try {
          if (typeof participantData.billing_data === 'string') {
            billingData = JSON.parse(participantData.billing_data);
          } else {
            billingData = participantData.billing_data;
          }
          console.log('✅ billing_data parsé:', Object.keys(billingData));
          console.log('💰 Exemple billing_data:', Object.values(billingData)[0]);
        } catch (error) {
          console.warn('⚠️ Erreur parsing billing_data:', error);
          billingData = {};
        }
      } else {
        console.warn('⚠️ Aucune billing_data trouvée pour ce participant');
      }

      // Générer la liste des mois dans la période
      const months = generateMonthsInPeriod(selectedPeriod.startMonth, selectedPeriod.endMonth);
      console.log('📅 Mois dans la période:', months);

      // Initialiser les totaux pour tous les participants
      const participantDetails: any[] = [];
      let groupTotalVolumePartage = 0;
      let groupTotalVolumeComplementaire = 0;
      let groupTotalInjectionPartagee = 0;
      let groupTotalInjectionComplementaire = 0;
      let groupTotalNetworkCosts = {
        utilisationReseau: 0,
        surcharges: 0,
        tarifCapacitaire: 0,
        tarifMesure: 0,
        tarifOSP: 0,
        transportELIA: 0,
        redevanceVoirie: 0,
        totalFraisReseau: 0
      };

      // Traiter chaque participant (individuel ou groupe)
      for (const currentParticipant of allParticipants) {
        console.log(`🔍 Traitement participant: ${currentParticipant.name}`);
        
        let participantVolumePartage = 0;
        let participantVolumeComplementaire = 0;
        let participantInjectionPartagee = 0;
        let participantInjectionComplementaire = 0;
        let participantNetworkCosts = {
          utilisationReseau: 0,
          surcharges: 0,
          tarifCapacitaire: 0,
          tarifMesure: 0,
          tarifOSP: 0,
          transportELIA: 0,
          redevanceVoirie: 0,
          totalFraisReseau: 0
        };

        if (currentParticipant.monthly_data) {
          let monthlyData;
          try {
            if (typeof currentParticipant.monthly_data === 'string') {
              monthlyData = JSON.parse(currentParticipant.monthly_data);
            } else {
              monthlyData = currentParticipant.monthly_data;
            }
          } catch (error) {
            console.warn(`Erreur parsing monthly_data pour ${currentParticipant.name}:`, error);
            monthlyData = {};
          }

          months.forEach(month => {
            const monthData = monthlyData[month];
            if (monthData) {
              participantVolumePartage += Number(monthData.volume_partage || 0);
              participantVolumeComplementaire += Number(monthData.volume_complementaire || 0);
              participantInjectionPartagee += Number(monthData.injection_partagee || 0);
              participantInjectionComplementaire += Number(monthData.injection_complementaire || 0);
            }
          });
        }

        // Traiter les données de facturation (coûts réseau)
        if (currentParticipant.billing_data) {
          let billingData;
          try {
            if (typeof currentParticipant.billing_data === 'string') {
              billingData = JSON.parse(currentParticipant.billing_data);
            } else {
              billingData = currentParticipant.billing_data;
            }
          } catch (error) {
            console.warn(`Erreur parsing billing_data pour ${currentParticipant.name}:`, error);
            billingData = {};
          }

          months.forEach(month => {
            const monthBilling = billingData[month];
            if (monthBilling && monthBilling.networkCosts) {
              const costs = monthBilling.networkCosts;
              participantNetworkCosts.utilisationReseau += Number(costs.utilisationReseau || 0);
              participantNetworkCosts.surcharges += Number(costs.surcharges || 0);
              participantNetworkCosts.tarifCapacitaire += Number(costs.tarifCapacitaire || 0);
              participantNetworkCosts.tarifMesure += Number(costs.tarifMesure || 0);
              participantNetworkCosts.tarifOSP += Number(costs.tarifOSP || 0);
              participantNetworkCosts.transportELIA += Number(costs.transportELIA || 0);
              participantNetworkCosts.redevanceVoirie += Number(costs.redevanceVoirie || 0);
              participantNetworkCosts.totalFraisReseau += Number(costs.totalFraisReseau || 0);
            }
          });
        }

        // Ajouter les détails de ce participant
        participantDetails.push({
          name: currentParticipant.name,
          address: currentParticipant.address,
          ean_code: currentParticipant.ean_code,
          type: currentParticipant.type,
          volume_partage: participantVolumePartage,
          volume_complementaire: participantVolumeComplementaire,
          injection_partagee: participantInjectionPartagee,
          injection_complementaire: participantInjectionComplementaire,
          networkCosts: participantNetworkCosts
        });

        // Ajouter aux totaux du groupe
        groupTotalVolumePartage += participantVolumePartage;
        groupTotalVolumeComplementaire += participantVolumeComplementaire;
        groupTotalInjectionPartagee += participantInjectionPartagee;
        groupTotalInjectionComplementaire += participantInjectionComplementaire;
        
        // Ajouter aux coûts réseau du groupe
        groupTotalNetworkCosts.utilisationReseau += participantNetworkCosts.utilisationReseau;
        groupTotalNetworkCosts.surcharges += participantNetworkCosts.surcharges;
        groupTotalNetworkCosts.tarifCapacitaire += participantNetworkCosts.tarifCapacitaire;
        groupTotalNetworkCosts.tarifMesure += participantNetworkCosts.tarifMesure;
        groupTotalNetworkCosts.tarifOSP += participantNetworkCosts.tarifOSP;
        groupTotalNetworkCosts.transportELIA += participantNetworkCosts.transportELIA;
        groupTotalNetworkCosts.redevanceVoirie += participantNetworkCosts.redevanceVoirie;
        groupTotalNetworkCosts.totalFraisReseau += participantNetworkCosts.totalFraisReseau;
      }

      console.log('📊 Totaux du groupe calculés:', {
        participants: participantDetails.length,
        totalVolumePartage: groupTotalVolumePartage,
        totalVolumeComplementaire: groupTotalVolumeComplementaire,
        totalInjectionPartagee: groupTotalInjectionPartagee,
        totalInjectionComplementaire: groupTotalInjectionComplementaire,
        totalNetworkCosts: groupTotalNetworkCosts.totalFraisReseau
      });

      // Filtrer les données pour la période sélectionnée
      // For group invoices, aggregate monthly data across ALL participants so
      // that per-month price resolution uses the correct group volumes.
      const periodMonthlyData: { [month: string]: MonthlyData } = {};
      const periodBillingData: { [month: string]: BillingData } = {};

      if (isGroup) {
        // Aggregate per-month volumes across all group participants
        months.forEach(month => {
          let aggVolPartage = 0;
          let aggVolCompl = 0;
          let aggInjPartagee = 0;
          let aggInjCompl = 0;
          let found = false;

          for (const cp of allParticipants) {
            if (!cp.monthly_data) continue;
            let cpMd: any;
            try {
              cpMd = typeof cp.monthly_data === 'string' ? JSON.parse(cp.monthly_data) : cp.monthly_data;
            } catch { continue; }
            const d = cpMd[month];
            if (d) {
              aggVolPartage += Number(d.volume_partage || 0);
              aggVolCompl += Number(d.volume_complementaire || 0);
              aggInjPartagee += Number(d.injection_partagee || 0);
              aggInjCompl += Number(d.injection_complementaire || 0);
              found = true;
            }
          }

          if (found) {
            periodMonthlyData[month] = {
              volume_partage: aggVolPartage,
              volume_complementaire: aggVolCompl,
              injection_partagee: aggInjPartagee,
              injection_complementaire: aggInjCompl
            } as MonthlyData;
          }
        });

        // Billing data: use the first participant's billing data for network costs
        months.forEach(month => {
          if (billingData[month]) {
            periodBillingData[month] = billingData[month];
          }
        });
      } else {
        months.forEach(month => {
          if (monthlyData[month]) {
            periodMonthlyData[month] = monthlyData[month];
            console.log(`📊 Données mensuelles ${month}:`, monthlyData[month]);
          } else {
            console.warn(`⚠️ Aucune donnée mensuelle pour ${month}`);
          }

          if (billingData[month]) {
            periodBillingData[month] = billingData[month];
            console.log(`💰 Données billing ${month}:`, billingData[month]);
          } else {
            console.warn(`⚠️ Aucune donnée billing pour ${month}`);
          }
        });
      }

      // Calculer les totaux (utiliser les totaux de groupe si c'est une facture de groupe)
      const totals = isGroup ? {
        volume_partage: groupTotalVolumePartage,
        injection_partagee: groupTotalInjectionPartagee,
        injection_complementaire: groupTotalInjectionComplementaire,
        networkCosts: groupTotalNetworkCosts
      } : calculateTotals(periodMonthlyData, periodBillingData);
      console.log('📊 Totaux calculés:', totals);

      // Fetch tariff overrides for all participants in the invoice
      const allParticipantIds = allParticipants.map((p: any) => p.id).filter(Boolean);
      const overridesMap = await fetchTariffOverridesBatch(allParticipantIds);
      const allParticipantsWithOverrides = allParticipants.map((p: any) => ({
        ...p,
        _tariffOverrides: overridesMap[p.id] || []
      }));

      // Attach overrides to the main participant data so calculateFinancialAmounts can use them
      const participantDataWithOverrides = {
        ...participantData,
        _tariffOverrides: overridesMap[participantData.id] || []
      };

      // Charger les valeurs d'indexation pour tous les paramètres X utilisés (prix variables)
      const xParams = Array.from(new Set(
        allParticipantsWithOverrides
          .map((m: any) => (m.shared_energy_formula_type === 'variable' ? m.shared_energy_x_param : null))
          .filter(Boolean)
      )) as string[];
      const idxValuesByParam: Record<string, Record<string, number>> = {};
      if (xParams.length > 0) {
        const { data: idxRows, error: idxError } = await supabase
          .from('indexation_parameters')
          .select('param_name, month_key, value')
          .in('param_name', xParams);
        if (idxError) throw idxError;
        for (const row of idxRows || []) {
          if (!idxValuesByParam[row.param_name]) idxValuesByParam[row.param_name] = {};
          idxValuesByParam[row.param_name][row.month_key] = Number(row.value);
        }
      }

      const calculations = calculateFinancialAmounts(totals, participantDataWithOverrides as any, selectedPeriod, includeMembershipFee, allParticipantsWithOverrides, periodMonthlyData, idxValuesByParam);
      console.log('💰 Calculs financiers:', calculations);

      const invoiceData: InvoiceData = {
        participant: isGroup ? {
          ...participantDataWithOverrides,
          name: removeGroupePrefix(participant.groupe),
          address: 'Multiples adresses',
          ean_code: 'Multiples codes EAN'
        } : participantDataWithOverrides,
        period: {
          startMonth: selectedPeriod.startMonth,
          endMonth: selectedPeriod.endMonth,
          startDate: selectedPeriod.startMonth + '-01',
          endDate: getLastDayOfMonth(selectedPeriod.endMonth)
        },
        monthlyData: periodMonthlyData,
        billingData: periodBillingData,
        totals,
        calculations
      };

      setInvoiceData(invoiceData);
      setParticipantNetworkDetails(participantDetails);
      console.log('🧾 Facture générée avec succès');

    } catch (error) {
      console.error('❌ Erreur génération facture:', error);
      setError(error.message || 'Erreur lors de la génération de la facture');
      toast.error('Erreur lors de la génération de la facture');
    } finally {
      setLoading(false);
    }
  };

  const generateMonthsInPeriod = (startMonth: string, endMonth: string): string[] => {
    const months: string[] = [];
    let current = startMonth;
    while (current <= endMonth) {
      months.push(current);
      const [y, m] = current.split('-').map(Number);
      current = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
    }
    return months;
  };

  const getLastDayOfMonth = (monthStr: string): string => {
    const [year, month] = monthStr.split('-').map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    return `${monthStr}-${String(lastDay).padStart(2, '0')}`;
  };

  const calculateTotals = (
    monthlyData: { [month: string]: MonthlyData },
    billingData: { [month: string]: BillingData }
  ) => {
    console.log('🧮 CALCUL DES TOTAUX');
    console.log('📊 Données mensuelles à traiter:', Object.keys(monthlyData));
    console.log('💰 Données billing à traiter:', Object.keys(billingData));

    const totals = {
      volume_partage: 0,
      volume_complementaire: 0,
      injection_partagee: 0,
      injection_complementaire: 0,
      networkCosts: {
        utilisationReseau: 0,
        surcharges: 0,
        tarifCapacitaire: 0,
        tarifMesure: 0,
        tarifOSP: 0,
        transportELIA: 0,
        redevanceVoirie: 0,
        totalFraisReseau: 0
      }
    };

    // Sommer les données mensuelles
    Object.entries(monthlyData).forEach(([month, data]) => {
      console.log(`📊 Ajout données ${month}:`, data);
      totals.volume_partage += Number(data.volume_partage || 0);
      totals.volume_complementaire += Number(data.volume_complementaire || 0);
      totals.injection_partagee += Number(data.injection_partagee || 0);
      totals.injection_complementaire += Number(data.injection_complementaire || 0);
    });

    // Sommer les coûts réseau depuis billing_data
    Object.entries(billingData).forEach(([month, data]) => {
      console.log(`💰 Ajout coûts réseau ${month}:`, data.networkCosts);
      if (data.networkCosts) {
        totals.networkCosts.utilisationReseau += Number(data.networkCosts.utilisationReseau || 0);
        totals.networkCosts.surcharges += Number(data.networkCosts.surcharges || 0);
        totals.networkCosts.tarifCapacitaire += Number(data.networkCosts.tarifCapacitaire || 0);
        totals.networkCosts.tarifMesure += Number(data.networkCosts.tarifMesure || 0);
        totals.networkCosts.tarifOSP += Number(data.networkCosts.tarifOSP || 0);
        totals.networkCosts.transportELIA += Number(data.networkCosts.transportELIA || 0);
        totals.networkCosts.redevanceVoirie += Number(data.networkCosts.redevanceVoirie || 0);
        totals.networkCosts.totalFraisReseau += Number(data.networkCosts.totalFraisReseau || 0);
      }
    });

    console.log('📊 Totaux finaux:', totals);
    return totals;
  };

  const calculateAnnualVolume = (participant: any, year: string, allGroupParticipants: any[] = []): number => {
    // Si c'est un groupe avec plusieurs participants
    if (allGroupParticipants.length > 1) {
      let totalVolume = 0;

      allGroupParticipants.forEach((p: any) => {
        if (!p.monthly_data) {
          return;
        }

        let monthlyData;
        try {
          if (typeof p.monthly_data === 'string') {
            monthlyData = JSON.parse(p.monthly_data);
          } else {
            monthlyData = p.monthly_data;
          }
        } catch (error) {
          console.warn(`⚠️ Erreur parsing monthly_data pour ${p.name}:`, error);
          return;
        }

        // Déterminer la date de début pour ce participant
        let startMonth = `${year}-01`;
        if (p.entry_date) {
          const entryYear = p.entry_date.substring(0, 4);
          const entryMonth = p.entry_date.substring(0, 7);

          // Si la date d'entrée est dans l'année concernée, commencer à partir de cette date
          if (entryYear === year) {
            startMonth = entryMonth;
          }
        }

        // Calculer le volume depuis la date d'entrée jusqu'à la fin de l'année
        Object.entries(monthlyData).forEach(([month, data]: [string, any]) => {
          if (month >= startMonth && month.startsWith(year)) {
            totalVolume += Number(data.volume_partage || 0);
          }
        });
      });

      return totalVolume / 1000; // Convertir en MWh
    }

    // Participant individuel
    if (!participant.monthly_data) {
      return 0;
    }

    let monthlyData;
    try {
      if (typeof participant.monthly_data === 'string') {
        monthlyData = JSON.parse(participant.monthly_data);
      } else {
        monthlyData = participant.monthly_data;
      }
    } catch (error) {
      console.warn('⚠️ Erreur parsing monthly_data pour calcul annuel:', error);
      return 0;
    }

    // Déterminer la date de début pour ce participant
    let startMonth = `${year}-01`;
    if (participant.entry_date) {
      const entryYear = participant.entry_date.substring(0, 4);
      const entryMonth = participant.entry_date.substring(0, 7);

      // Si la date d'entrée est dans l'année concernée, commencer à partir de cette date
      if (entryYear === year) {
        startMonth = entryMonth;
      }
    }

    let totalAnnualVolume = 0;
    Object.entries(monthlyData).forEach(([month, data]: [string, any]) => {
      if (month >= startMonth && month.startsWith(year)) {
        totalAnnualVolume += Number(data.volume_partage || 0);
      }
    });

    return totalAnnualVolume / 1000; // Convertir en MWh
  };

  const calculateExciseCosts = (volumePartageInMWh: number, annualConsumption: number, isCompany: boolean, vatRate: number, useAnnualVolume: boolean = false, participant?: any, year?: string, allGroupParticipants: any[] = []): ExciseCosts => {
    let droitAcciseRate = 0;
    const cotisationEnergieRate = billingConfig.cotisation_energie ?? 1.9261;

    if (isCompany) {
      if (annualConsumption >= 0 && annualConsumption <= 20) {
        droitAcciseRate = billingConfig.accise_ent_t1 ?? 14.21;
      } else if (annualConsumption > 20 && annualConsumption <= 50) {
        droitAcciseRate = billingConfig.accise_ent_t2 ?? 12.09;
      } else if (annualConsumption > 50 && annualConsumption <= 1000) {
        droitAcciseRate = billingConfig.accise_ent_t3 ?? 11.39;
      } else if (annualConsumption > 1000 && annualConsumption <= 25000) {
        droitAcciseRate = billingConfig.accise_ent_t4 ?? 10.69;
      }
    } else {
      if (annualConsumption >= 0 && annualConsumption <= 20) {
        droitAcciseRate = billingConfig.accise_part_t1 ?? 47.48;
      } else if (annualConsumption > 20 && annualConsumption <= 50) {
        droitAcciseRate = billingConfig.accise_part_t2 ?? 45.46;
      } else if (annualConsumption > 50 && annualConsumption <= 1000) {
        droitAcciseRate = billingConfig.accise_part_t3 ?? 44.78;
      } else if (annualConsumption > 1000 && annualConsumption <= 25000) {
        droitAcciseRate = billingConfig.accise_part_t4 ?? 44.11;
      }
    }

    // Utiliser le volume annuel si la régularisation est activée
    const volumeToUse = useAnnualVolume && participant && year
      ? calculateAnnualVolume(participant, year, allGroupParticipants)
      : volumePartageInMWh;

    console.log('💰 Calcul des accises:', {
      volumePartageInMWh,
      volumeToUse,
      useAnnualVolume,
      droitAcciseRate,
      cotisationEnergieRate
    });

    const droitAccise = volumeToUse * droitAcciseRate;
    const cotisationEnergie = volumeToUse * cotisationEnergieRate;
    const totalExcise = droitAccise + cotisationEnergie;
    const totalExciseTVAC = totalExcise * (1 + vatRate);

    return {
      droitAccise: Math.round(droitAccise * 100) / 100,
      cotisationEnergie: Math.round(cotisationEnergie * 100) / 100,
      totalExcise: Math.round(totalExcise * 100) / 100,
      totalExciseTVAC: Math.round(totalExciseTVAC * 100) / 100
    };
  };

  const calculateFinancialAmounts = (totals: any, participant: Participant, period: { startMonth: string; endMonth: string }, forceIncludeMembershipFee?: boolean, allGroupParticipants: any[] = [], monthlyDataForPeriod?: { [month: string]: MonthlyData }, idxValuesByParam: Record<string, Record<string, number>> = {}) => {
    console.log('💰 CALCUL DES MONTANTS FINANCIERS');

    const p = participant as any;
    const isGroupInvoice = allGroupParticipants.length > 1;
    // For group invoices, find a prosumer to get the sell price (prosumer_sell_price)
    const prosumerInGroup = allGroupParticipants.find((gp: any) => gp.type === 'prosumer') || null;
    const isCompany = !!participant.company_number;
    const vatRate = p.vat_rate != null
      ? Number(p.vat_rate)
      : (isCompany ? (billingConfig.tva_entreprise ?? 0.21) : (billingConfig.tva_particulier ?? 0.06));

    const volumePartageInMWh = totals.volume_partage / 1000;
    const injectionPartageeInMWh = totals.injection_partagee / 1000;
    const injectionComplementaireInMWh = totals.injection_complementaire / 1000;

    let energySharedCostHTVA: number;
    let injectionRevenue: number = 0;

    if (isGroupInvoice) {
      // Per-member calculation: each member uses its OWN price fields and overrides.
      //   - consumer: buy = shared_energy_price, sell = shared_energy_price (no injection normally)
      //   - prosumer: buy = prosumer_buy_price, sell = prosumer_sell_price
      //   - producer: buy = shared_energy_price, sell = shared_energy_price
      energySharedCostHTVA = 0;
      injectionRevenue = 0;
      const periodMonths = generateMonthsInPeriod(period.startMonth, period.endMonth);

      for (const member of allGroupParticipants) {
        const mOverrides = member._tariffOverrides || [];
        const isMemberProsumer = member.type === 'prosumer' && member.prosumer_buy_price != null;

        let memberMD: any = {};
        try {
          memberMD = typeof member.monthly_data === 'string'
            ? JSON.parse(member.monthly_data || '{}')
            : (member.monthly_data || {});
        } catch { continue; }

        for (const month of periodMonths) {
          const d = memberMD[month];
          if (!d) continue;
          const monthVolumeMWh = Number(d.volume_partage || 0) / 1000;
          const monthInjMWh = Number(d.injection_partagee || 0) / 1000;

          // Buy price
          let buyPrice: number;
          if (isMemberProsumer) {
            const buyOverrides = mOverrides.filter((o: TariffOverride) => o.price_type === 'prosumer_buy_price');
            buyPrice = resolvePriceForMonth(member.prosumer_buy_price, buyOverrides, month);
          } else if (member.shared_energy_formula_type === 'variable' && member.shared_energy_a && member.shared_energy_x_param) {
            const a = parseFloat(member.shared_energy_a) || 0;
            const xVal = idxValuesByParam[member.shared_energy_x_param]?.[month];
            buyPrice = xVal !== undefined ? a * xVal : Number(member.shared_energy_price || 100);
          } else {
            const buyOverrides = mOverrides.filter((o: TariffOverride) => o.price_type === 'shared_energy_price');
            buyPrice = resolvePriceForMonth(member.shared_energy_price ?? null, buyOverrides, month);
          }
          energySharedCostHTVA += monthVolumeMWh * buyPrice;

          // Sell price
          if (monthInjMWh > 0) {
            let sellPrice: number;
            if (isMemberProsumer && member.prosumer_sell_price != null) {
              const sellOverrides = mOverrides.filter((o: TariffOverride) => o.price_type === 'prosumer_sell_price');
              sellPrice = resolvePriceForMonth(member.prosumer_sell_price, sellOverrides, month);
            } else if (member.shared_energy_formula_type === 'variable' && member.shared_energy_a && member.shared_energy_x_param) {
              sellPrice = buyPrice;
            } else {
              const sellOverrides = mOverrides.filter((o: TariffOverride) => o.price_type === 'shared_energy_price');
              sellPrice = resolvePriceForMonth(member.shared_energy_price ?? null, sellOverrides, month);
            }
            injectionRevenue += monthInjMWh * sellPrice;
          }
        }
      }
    } else if (p.type === 'prosumer' && p.prosumer_buy_price != null) {
      // Individual prosumer: use prosumer_buy_price
      if (monthlyDataForPeriod) {
        const overrides: TariffOverride[] = (p._tariffOverrides || []).filter((o: TariffOverride) => o.price_type === 'prosumer_buy_price');
        energySharedCostHTVA = 0;
        Object.entries(monthlyDataForPeriod).forEach(([monthKey, monthData]) => {
          const monthPrice = resolvePriceForMonth(p.prosumer_buy_price, overrides, monthKey);
          const monthVolumeMWh = Number(monthData.volume_partage || 0) / 1000;
          energySharedCostHTVA += monthVolumeMWh * monthPrice;
        });
      } else {
        energySharedCostHTVA = volumePartageInMWh * Number(p.prosumer_buy_price);
      }
    } else if (p.shared_energy_formula_type === 'variable' && p.shared_energy_a && p.shared_energy_x_param && monthlyDataForPeriod) {
      const a = parseFloat(p.shared_energy_a) || 0;
      energySharedCostHTVA = 0;
      Object.entries(monthlyDataForPeriod).forEach(([monthKey, monthData]) => {
        const xVal = idxValuesByParam[p.shared_energy_x_param]?.[monthKey];
        if (xVal !== undefined) {
          const monthPrice = a * xVal;
          const monthVolumeMWh = Number(monthData.volume_partage || 0) / 1000;
          energySharedCostHTVA += monthVolumeMWh * monthPrice;
        } else {
          const fallback = Number(p.shared_energy_price || 100);
          const monthVolumeMWh = Number(monthData.volume_partage || 0) / 1000;
          energySharedCostHTVA += monthVolumeMWh * fallback;
        }
      });
    } else if (monthlyDataForPeriod) {
      const overrides: TariffOverride[] = (p._tariffOverrides || []).filter((o: TariffOverride) => o.price_type === 'shared_energy_price');
      energySharedCostHTVA = 0;
      Object.entries(monthlyDataForPeriod).forEach(([monthKey, monthData]) => {
        const monthPrice = resolvePriceForMonth(p.shared_energy_price ?? null, overrides, monthKey);
        const monthVolumeMWh = Number(monthData.volume_partage || 0) / 1000;
        energySharedCostHTVA += monthVolumeMWh * monthPrice;
      });
    } else {
      const sharedEnergyPrice = Number(participant.shared_energy_price || 100);
      energySharedCostHTVA = volumePartageInMWh * sharedEnergyPrice;
    }

    const energySharedCostTVAC = energySharedCostHTVA * (1 + vatRate);

    // Utiliser les coûts réseau réels depuis billing_data
    const networkCostTotal = totals.networkCosts.totalFraisReseau || 0;
    const networkCostTVAC = networkCostTotal * (1 + vatRate); // Utiliser le même taux de TVA que l'énergie

    // Calculer les accises
    const year = period.startMonth.split('-')[0];
    let exciseCosts: ExciseCosts;
    if (isGroupInvoice) {
      let totalDroitAccise = 0;
      let totalCotisationEnergie = 0;
      const periodMonths = generateMonthsInPeriod(period.startMonth, period.endMonth);
      for (const member of allGroupParticipants) {
        let memberMD: any = {};
        try {
          memberMD = typeof member.monthly_data === 'string'
            ? JSON.parse(member.monthly_data || '{}')
            : (member.monthly_data || {});
        } catch { continue; }
        let memberVolPartage = 0;
        for (const month of periodMonths) {
          const d = memberMD[month];
          if (d) memberVolPartage += Number(d.volume_partage || 0);
        }
        const memberMWh = memberVolPartage / 1000;
        const memberAnnualConsumption = Number(member.annual_consumption || 30);
        const memberIsCompany = !!member.company_number;
        const memberExcise = calculateExciseCosts(memberMWh, memberAnnualConsumption, memberIsCompany, vatRate, isAnnualRegularization, member, year, []);
        totalDroitAccise += memberExcise.droitAccise;
        totalCotisationEnergie += memberExcise.cotisationEnergie;
      }
      const totalExcise = totalDroitAccise + totalCotisationEnergie;
      exciseCosts = {
        droitAccise: Math.round(totalDroitAccise * 100) / 100,
        cotisationEnergie: Math.round(totalCotisationEnergie * 100) / 100,
        totalExcise: Math.round(totalExcise * 100) / 100,
        totalExciseTVAC: Math.round(totalExcise * (1 + vatRate) * 100) / 100,
      };
    } else {
      const annualConsumption = Number(participant.annual_consumption || 30);
      exciseCosts = calculateExciseCosts(volumePartageInMWh, annualConsumption, isCompany, vatRate, isAnnualRegularization, participant, year, allGroupParticipants);
    }

    console.log('💰 Coûts calculés:', {
      energySharedCostHTVA,
      energySharedCostTVAC,
      networkCostTotal: `${networkCostTotal}€ HTVA`,
      networkCostTVAC: `${networkCostTVAC}€ TVAC`,
      exciseCosts: `${exciseCosts.totalExcise}€ HTVA, ${exciseCosts.totalExciseTVAC}€ TVAC`
    });

    if (!isGroupInvoice && p.type === 'prosumer' && p.prosumer_sell_price != null) {
      // Individual prosumer: use prosumer_sell_price
      if (monthlyDataForPeriod) {
        const overrides: TariffOverride[] = (p._tariffOverrides || []).filter((o: TariffOverride) => o.price_type === 'prosumer_sell_price');
        injectionRevenue = 0;
        Object.entries(monthlyDataForPeriod).forEach(([monthKey, monthData]) => {
          const monthPrice = resolvePriceForMonth(p.prosumer_sell_price, overrides, monthKey);
          const monthInjMWh = Number(monthData.injection_partagee || 0) / 1000;
          injectionRevenue += monthInjMWh * monthPrice;
        });
      } else {
        injectionRevenue = injectionPartageeInMWh * Number(p.prosumer_sell_price);
      }
    } else if (!isGroupInvoice && p.shared_energy_formula_type === 'variable' && p.shared_energy_a && p.shared_energy_x_param && monthlyDataForPeriod) {
      const a = parseFloat(p.shared_energy_a) || 0;
      injectionRevenue = 0;
      Object.entries(monthlyDataForPeriod).forEach(([monthKey, monthData]) => {
        const xVal = idxValuesByParam[p.shared_energy_x_param]?.[monthKey];
        const monthPrice = xVal !== undefined ? a * xVal : Number(p.shared_energy_price || 100);
        const monthInjMWh = Number(monthData.injection_partagee || 0) / 1000;
        injectionRevenue += monthInjMWh * monthPrice;
      });
    } else if (!isGroupInvoice && monthlyDataForPeriod) {
      const overrides: TariffOverride[] = (p._tariffOverrides || []).filter((o: TariffOverride) => o.price_type === 'shared_energy_price');
      injectionRevenue = 0;
      Object.entries(monthlyDataForPeriod).forEach(([monthKey, monthData]) => {
        const monthPrice = resolvePriceForMonth(p.shared_energy_price ?? null, overrides, monthKey);
        const monthInjMWh = Number(monthData.injection_partagee || 0) / 1000;
        injectionRevenue += monthInjMWh * monthPrice;
      });
    } else if (!isGroupInvoice) {
      const sharedEnergyPrice = Number(participant.shared_energy_price || 100);
      injectionRevenue = injectionPartageeInMWh * sharedEnergyPrice;
    }

    // Frais d'adhésion annuels
    let shouldIncludeMembershipFee = false;
    let membershipFeeAnniversaryYear: number | null = null;

    // Si forceIncludeMembershipFee est défini (pour les groupes), l'utiliser
    if (forceIncludeMembershipFee !== undefined) {
      shouldIncludeMembershipFee = forceIncludeMembershipFee;
      console.log(`💰 Frais d'adhésion forcé par l'utilisateur: ${shouldIncludeMembershipFee}`);
    } else {
      // Pour les factures de groupe, utiliser la date d'entrée la plus ancienne
      // parmi tous les participants de la même entreprise pour déterminer le mois anniversaire.
      // Cela évite de facturer la cotisation pour un nouveau membre dont la date d'entrée
      // est différente de celle des membres existants qui ont déjà été facturés.
      let entryDateForAnniversary: string | null = participant.entry_date || null;

      if (isGroupInvoice && allGroupParticipants.length > 0) {
        const sameCompanyParticipants = p.company_number
          ? allGroupParticipants.filter((gp: any) => gp.company_number === p.company_number)
          : allGroupParticipants;
        const allEntryDates = sameCompanyParticipants
          .map((gp: any) => gp.entry_date)
          .filter((d: any) => d)
          .sort((a: string, b: string) => a.localeCompare(b));
        if (allEntryDates.length > 0) {
          entryDateForAnniversary = allEntryDates[0];
          console.log(`💰 Date d'entrée la plus ancienne pour le groupe/entreprise: ${entryDateForAnniversary}`);
        }
      }

      if (entryDateForAnniversary) {
        const entryDate = parseISO(entryDateForAnniversary);
        const entryMonth = entryDate.getMonth(); // 0-11
        const entryDay = entryDate.getDate();

        const monthsInPeriod = generateMonthsInPeriod(period.startMonth, period.endMonth);

        const currentYear = new Date().getFullYear();
        const entryYear = entryDate.getFullYear();

        for (let year = entryYear; year <= currentYear + 1; year++) {
          const anniversaryMonth = format(new Date(year, entryMonth, entryDay), 'yyyy-MM');
          if (monthsInPeriod.includes(anniversaryMonth)) {
            shouldIncludeMembershipFee = true;
            membershipFeeAnniversaryYear = year;
            console.log(`✅ Mois d'anniversaire trouvé: ${anniversaryMonth} dans la période`);
            break;
          }
        }
      }
    }

    // Exception pour Simon Laffut - jamais de frais d'adhésion
    const isSimonLaffut = participant.name?.toLowerCase().includes('simon laffut');
    const adhesionFeeHT = (participant as any).adhesion_fee != null ? Number((participant as any).adhesion_fee) : 50;
    const membershipFeeHTVA = (shouldIncludeMembershipFee && !isSimonLaffut) ? adhesionFeeHT : 0;
    const membershipFeeTVAC = (shouldIncludeMembershipFee && !isSimonLaffut) ? adhesionFeeHT * (1 + vatRate) : 0;

    console.log('💰 Frais d\'adhésion:', {
      entry_date: participant.entry_date,
      shouldIncludeMembershipFee,
      forceIncludeMembershipFee,
      invoicePeriod: `${period.startMonth} à ${period.endMonth}`,
      membershipFeeHTVA: `${membershipFeeHTVA.toFixed(2)}€`,
      membershipFeeTVAC: `${membershipFeeTVAC}€`
    });
    // Total
    const totalCostTVAC = energySharedCostTVAC + networkCostTVAC + exciseCosts.totalExciseTVAC + membershipFeeTVAC;
    const injectionVatRate = p.vat_rate != null ? Number(p.vat_rate) : (isCompany ? (billingConfig.tva_entreprise ?? 0.21) : 0);
    const injectionRevenueTVAC = injectionRevenue * (1 + injectionVatRate);
    const netAmount = totalCostTVAC - injectionRevenueTVAC;

    const calculations = {
      energySharedCostHTVA: Math.round(energySharedCostHTVA * 100) / 100,
      energySharedCostTVAC: Math.round(energySharedCostTVAC * 100) / 100,
      networkCostTotal: Math.round(networkCostTotal * 100) / 100,
      networkCostTVAC: Math.round(networkCostTVAC * 100) / 100,
      exciseCosts: exciseCosts,
      membershipFeeHTVA: Math.round(membershipFeeHTVA * 100) / 100,
      membershipFeeTVAC: Math.round(membershipFeeTVAC * 100) / 100,
      isFirstInvoiceOfYear: shouldIncludeMembershipFee,
      membershipFeeYear: membershipFeeAnniversaryYear,
      totalCostTVAC: Math.round(totalCostTVAC * 100) / 100,
      injectionRevenue: Math.round(injectionRevenue * 100) / 100,
      injectionVatRate: injectionVatRate,
      netAmount: Math.round(netAmount * 100) / 100,
      vatRate: vatRate,
      prosumerBuyPrice: isGroupInvoice
        ? Number(p.shared_energy_price || 0)
        : (p.type === 'prosumer' && p.prosumer_buy_price != null ? Number(p.prosumer_buy_price) : null),
      prosumerSellPrice: isGroupInvoice && prosumerInGroup
        ? Number(prosumerInGroup.prosumer_sell_price)
        : (!isGroupInvoice && p.type === 'prosumer' && p.prosumer_sell_price != null ? Number(p.prosumer_sell_price) : null),
      averageBuyPrice: volumePartageInMWh > 0 ? Math.round((energySharedCostHTVA / volumePartageInMWh) * 100) / 100 : null,
      averageSellPrice: injectionPartageeInMWh > 0 ? Math.round((injectionRevenue / injectionPartageeInMWh) * 100) / 100 : null,
    };

    console.log('💰 Calculs finaux:', calculations);
    return calculations;
  };

  const handlePrint = () => {
    const printContent = document.getElementById('invoice-content');
    if (printContent) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Facture ${participant.name}</title>
              <style>
                body { 
                  font-family: Arial, sans-serif; 
                  margin: 20px; 
                  color: #000 !important;
                  background: white !important;
                }
                .no-print { display: none !important; }
                table { 
                  border-collapse: collapse; 
                  width: 100%; 
                  margin: 10px 0;
                }
                th, td { 
                  border: 1px solid #333 !important; 
                  padding: 8px; 
                  text-align: left;
                  color: #000 !important;
                  background: white !important;
                }
                th { 
                  background-color: #f0f0f0 !important;
                  font-weight: bold;
                  color: #000 !important;
                }
                .text-right { text-align: right; }
                .font-bold { font-weight: bold; }
                .text-lg { font-size: 1.125rem; }
                .text-xl { font-size: 1.25rem; }
                .text-2xl { font-size: 1.5rem; }
                .mb-4 { margin-bottom: 1rem; }
                .mb-6 { margin-bottom: 1.5rem; }
                .mb-8 { margin-bottom: 2rem; }
                .mt-4 { margin-top: 1rem; }
                .p-4 { padding: 1rem; }
                .bg-white { background-color: #f9fafb !important; }
                .border { border: 1px solid #333 !important; }
                .rounded { border-radius: 0.375rem; }
                .total-row {
                  background-color: #e5e7eb !important;
                  font-weight: bold;
                  color: #000 !important;
                }
              </style>
            </head>
            <body>
              ${printContent.innerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  const calculatePageBreaks = async () => {
    const invoiceElement = document.getElementById('invoice-content');
    if (!invoiceElement) return;

    const A4_HEIGHT_MM = 297;
    const A4_WIDTH_MM = 210;
    const MARGIN_MM = 5;
    const USABLE_HEIGHT_MM = A4_HEIGHT_MM - (MARGIN_MM * 2);

    const elementHeight = invoiceElement.scrollHeight;
    const elementWidth = invoiceElement.offsetWidth;
    const ratio = (A4_WIDTH_MM - MARGIN_MM * 2) / elementWidth;
    const pageHeightInPx = USABLE_HEIGHT_MM / ratio;

    const sections = invoiceElement.querySelectorAll('[data-section]');
    const sectionBoundaries: { start: number; end: number; height: number; name: string }[] = [];

    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      const invoiceRect = invoiceElement.getBoundingClientRect();
      const relativeTop = rect.top - invoiceRect.top + invoiceElement.scrollTop;
      const relativeBottom = relativeTop + rect.height;

      sectionBoundaries.push({
        start: Math.round(relativeTop),
        end: Math.round(relativeBottom),
        height: Math.round(rect.height),
        name: section.getAttribute('data-section') || 'unknown'
      });
    });

    const breaks: { position: number; pageNumber: number; reason: string }[] = [];
    let currentY = 0;
    let pageNumber = 1;

    while (currentY < elementHeight) {
      const targetEndY = currentY + pageHeightInPx;
      let actualEndY = targetEndY;
      let breakReason = 'Fin de page normale';

      for (const section of sectionBoundaries) {
        if (section.start >= currentY && section.start < targetEndY) {
          if (section.end > targetEndY) {
            if (section.height < pageHeightInPx * 0.95) {
              if (section.start > currentY + 100) {
                actualEndY = section.start;
                breakReason = `Section "${section.name}" reportee (${Math.round(section.height)}px < page ${Math.round(pageHeightInPx)}px)`;
                break;
              }
            } else {
              breakReason = `Section "${section.name}" coupee (${Math.round(section.height)}px > page ${Math.round(pageHeightInPx)}px)`;
            }
          }
        }
      }

      actualEndY = Math.min(actualEndY, elementHeight);

      if (actualEndY < elementHeight) {
        const invoiceRect = invoiceElement.getBoundingClientRect();
        const allTextElements = invoiceElement.querySelectorAll('td, th, p, span, div, h1, h2, h3, h4, h5, h6');
        let nearbyText = '';
        let closestDistance = Infinity;

        allTextElements.forEach((el) => {
          const rect = el.getBoundingClientRect();
          const relativeTop = rect.top - invoiceRect.top + invoiceElement.scrollTop;
          const relativeBottom = relativeTop + rect.height;

          if (relativeTop <= actualEndY && relativeBottom >= actualEndY - 50) {
            const distance = Math.abs(relativeBottom - actualEndY);
            const text = (el.textContent || '').trim();
            if (text.length > 0 && text.length < 100 && distance < closestDistance) {
              closestDistance = distance;
              nearbyText = text.substring(0, 40) + (text.length > 40 ? '...' : '');
            }
          }
        });

        breaks.push({
          position: actualEndY,
          pageNumber: pageNumber,
          reason: nearbyText ? `Apres: "${nearbyText}"` : breakReason
        });
      }

      currentY = actualEndY;
      pageNumber++;
    }

    setPageBreaks(breaks);
  };

  const togglePreviewMode = async () => {
    if (!previewMode) {
      await calculatePageBreaks();
    }
    setPreviewMode(!previewMode);
  };

  const getTextNearPosition = (position: number): string => {
    const invoiceElement = document.getElementById('invoice-content');
    if (!invoiceElement) return '';

    const invoiceRect = invoiceElement.getBoundingClientRect();
    const scrollTop = invoiceElement.scrollTop || 0;

    const allTextElements = invoiceElement.querySelectorAll('td, th, p, span, div, h1, h2, h3, h4, h5, h6');
    let closestText = '';
    let closestDistance = Infinity;

    allTextElements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const relativeTop = rect.top - invoiceRect.top + scrollTop;
      const relativeBottom = relativeTop + rect.height;

      if (relativeTop <= position && relativeBottom >= position - 50) {
        const distance = Math.abs(relativeBottom - position);
        const text = (el.textContent || '').trim();
        if (text.length > 0 && text.length < 100 && distance < closestDistance) {
          closestDistance = distance;
          closestText = text.substring(0, 40) + (text.length > 40 ? '...' : '');
        }
      }
    });

    return closestText || 'Position: ' + position + 'px';
  };

  const adjustPageBreak = (index: number, delta: number) => {
    setPageBreaks(prev => {
      const updated = [...prev];
      const newPosition = updated[index].position + delta;
      const invoiceElement = document.getElementById('invoice-content');
      const maxHeight = invoiceElement?.scrollHeight || 5000;

      const minPosition = index === 0 ? 100 : updated[index - 1].position + 100;
      const maxPosition = index < updated.length - 1 ? updated[index + 1].position - 100 : maxHeight - 100;

      const finalPosition = Math.max(minPosition, Math.min(maxPosition, newPosition));
      const nearbyText = getTextNearPosition(finalPosition);

      updated[index] = {
        ...updated[index],
        position: finalPosition,
        reason: nearbyText ? `Apres: "${nearbyText}"` : 'Ajustement manuel'
      };
      return updated;
    });
  };

  const addPageBreak = () => {
    const invoiceElement = document.getElementById('invoice-content');
    if (!invoiceElement) return;

    const elementHeight = invoiceElement.scrollHeight;
    let newPosition = elementHeight / 2;

    if (pageBreaks.length > 0) {
      const lastBreak = pageBreaks[pageBreaks.length - 1];
      newPosition = lastBreak.position + (elementHeight - lastBreak.position) / 2;
    }

    setPageBreaks(prev => [
      ...prev,
      {
        position: Math.round(newPosition),
        pageNumber: prev.length + 1,
        reason: 'Saut de page manuel'
      }
    ].sort((a, b) => a.position - b.position).map((pb, idx) => ({
      ...pb,
      pageNumber: idx + 1
    })));
  };

  const removePageBreak = (index: number) => {
    setPageBreaks(prev =>
      prev.filter((_, i) => i !== index).map((pb, idx) => ({
        ...pb,
        pageNumber: idx + 1
      }))
    );
  };

  const resetPageBreaks = async () => {
    await calculatePageBreaks();
  };

  const handleDownload = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (saving) {
      console.log('Telechargement deja en cours');
      return;
    }

    console.log('Demarrage du telechargement PDF');
    setSaving(true);
    setDownloadProgress(0);
    setDownloadStatus('Preparation...');

    setTimeout(async () => {
      let buttonsToHide: NodeListOf<Element> | null = null;

      try {
        setDownloadProgress(10);
        setDownloadStatus('Préparation du document...');

        const invoiceElement = document.getElementById('invoice-content');

        if (!invoiceElement) {
          console.error('Element invoice-content non trouvé');
          toast.error('Contenu de la facture introuvable');
          setSaving(false);
          setDownloadProgress(0);
          setDownloadStatus('');
          return;
        }

        setDownloadProgress(20);
        setDownloadStatus('Optimisation de la mise en page...');

        buttonsToHide = invoiceElement.querySelectorAll('.no-print');
        buttonsToHide.forEach((btn) => {
          (btn as HTMLElement).style.visibility = 'hidden';
        });

        await new Promise(resolve => setTimeout(resolve, 150));

        setDownloadProgress(40);
        setDownloadStatus('Capture des sections...');

        const pdf = await captureGroupedPDF(invoiceElement, (p) => setDownloadProgress(p), true);

        setDownloadProgress(85);
        setDownloadStatus('Génération du PDF...');

        if (buttonsToHide) {
          buttonsToHide.forEach((btn) => {
            (btn as HTMLElement).style.visibility = 'visible';
          });
        }

        setDownloadProgress(95);
        setDownloadStatus('Finalisation...');

        const participantName = isGroupInvoice
          ? removeGroupePrefix(participant.groupe)
          : invoiceData.participant.name;
        const cleanName = normalizeFileName(participantName);
        const periodStr = invoiceData.period.startMonth !== invoiceData.period.endMonth
          ? `${invoiceData.period.startMonth}-${invoiceData.period.endMonth}`
          : invoiceData.period.startMonth;
        const fileName = `Facture_${cleanName}_${periodStr}.pdf`;

        pdf.save(fileName);

        setDownloadProgress(100);
        setDownloadStatus('Téléchargement terminé !');
        toast.success('Facture téléchargée avec succès');

        setTimeout(() => {
          setDownloadProgress(0);
          setDownloadStatus('');
          setSaving(false);
        }, 2000);

      } catch (error) {
        console.error('Erreur lors de la génération du PDF:', error);
        toast.error('Erreur lors de la génération du PDF. Veuillez réessayer.');

        if (buttonsToHide) {
          buttonsToHide.forEach((btn) => {
            (btn as HTMLElement).style.visibility = 'visible';
          });
        }

        setDownloadProgress(0);
        setDownloadStatus('');
        setSaving(false);
      }
    }, 100);
  };

  const handleSaveInvoice = async () => {
    try {
      setSaving(true);
      
      // Créer les données de la facture à sauvegarder
      const invoiceRecord = {
        id: `invoice_${invoiceData.participant.id}_${invoiceData.period.startMonth}_${invoiceData.period.endMonth}`,
        participant_id: invoiceData.participant.id,
        participant_name: invoiceData.participant.name,
        period_start: invoiceData.period.startMonth,
        period_end: invoiceData.period.endMonth,
        generated_date: new Date().toISOString(),
        totals: invoiceData.totals,
        calculations: invoiceData.calculations,
        status: 'generated'
      };

      // Récupérer les données billing actuelles
      const { data: currentParticipant, error: fetchError } = await supabase
        .from('participants')
        .select('billing_data')
        .eq('id', invoiceData.participant.id)
        .single();

      if (fetchError) {
        throw new Error(`Erreur lors de la récupération des données: ${fetchError.message}`);
      }

      // Parser les données billing existantes
      let billingData = {};
      if (currentParticipant.billing_data) {
        try {
          if (typeof currentParticipant.billing_data === 'string') {
            billingData = JSON.parse(currentParticipant.billing_data);
          } else {
            billingData = currentParticipant.billing_data;
          }
        } catch (error) {
          console.warn('Erreur parsing billing_data existant:', error);
          billingData = {};
        }
      }

      // Ajouter la facture aux données billing
      const updatedBillingData = {
        ...billingData,
        invoices: {
          ...(billingData.invoices || {}),
          [invoiceRecord.id]: invoiceRecord
        }
      };

      // Pas besoin de pré-calculer les totaux, ils seront calculés à la volée dans le tableau
      // comme dans le MemberDashboard
      console.log('✅ Données du groupe prêtes pour affichage');

      // Sauvegarder en base
      const { error: updateError } = await supabase
        .from('participants')
        .update({ billing_data: updatedBillingData })
        .eq('id', invoiceData.participant.id);

      if (updateError) {
        throw new Error(`Erreur lors de la sauvegarde: ${updateError.message}`);
      }

      toast.success('Facture enregistrée avec succès');
    } catch (error) {
      console.error('Erreur sauvegarde facture:', error);
      toast.error(`Erreur lors de l'enregistrement: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-neutral-800">Génération de la facture...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-brand-flame/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-brand-flame" />
          </div>
          <h3 className="text-lg font-semibold text-brand-flame mb-2">Erreur</h3>
          <p className="text-brand-flame mb-4">{error}</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-brand-flame/10 text-white rounded-lg hover:bg-brand-flame/10 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    );
  }

  if (!invoiceData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl p-8 text-center max-w-md">
          <p className="text-neutral-800">Aucune donnée disponible pour cette période</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    );
  }

  // Numéro : celui de la facture émise depuis le CRM, sinon l'ancien format (fin de l'EAN + mois).
  const legacyNumber = `${isGroupInvoice && groupParticipants && groupParticipants.length > 0
    ? (groupParticipants[0].ean_code ? groupParticipants[0].ean_code.slice(-5) : '00000')
    : (invoiceData.participant.ean_code?.slice(-6) || '000000')}-${format(parseISO(invoiceData.period.startDate), 'MM-yy')}`;
  const displayNumber = billing?.invoiceNumber || legacyNumber;
  /** Domiciliation : seulement si un montant est à payer. */
  const directDebit = billing?.channel === 'direct_debit' && billing.collectionDate && invoiceData.calculations.netAmount > 0 ? billing : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* Header avec boutons d'action */}
        <div className="no-print flex items-center justify-between p-6 border-b border-neutral-300 bg-neutral-50">
          <div className="flex items-center space-x-3">
            <FileText className="w-6 h-6 text-amber-600" />
            <div>
              <h2 className="text-xl font-bold text-neutral-900">
                {invoiceData?.calculations?.netAmount < 0 ? "Autofacturation" : 'Facture énergétique'}
              </h2>
              <p className="text-sm text-neutral-700">
                {format(parseISO(invoiceData.period.startDate), 'MMMM yyyy', { locale: fr })}
                {invoiceData.period.startMonth !== invoiceData.period.endMonth && 
                  ` - ${format(parseISO(invoiceData.period.endDate), 'MMMM yyyy', { locale: fr })}`
                }
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-3">
            {/* Bouton régularisation annuelle */}
            {isAdminOnly && (
            <button
              onClick={() => setIsAnnualRegularization(!isAnnualRegularization)}
              disabled={saving || participant?.type === 'producer'}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                isAnnualRegularization
                  ? 'bg-amber-500 text-white hover:bg-amber-600'
                  : 'bg-neutral-200 text-neutral-700 hover:bg-neutral-300'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              title={participant?.type === 'producer' ? 'Non applicable pour les producteurs' : 'Calculer les accises sur base des volumes annuels'}
            >
              <RotateCcw className="w-4 h-4" />
              <span>{isAnnualRegularization ? 'Régularisation activée' : 'Régularisation annuelle des accises'}</span>
            </button>
            )}

            <div className="flex items-center space-x-3">
              {isAdminOnly && (
              <button
                onClick={handlePrint}
                disabled={saving}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-400 transition-colors flex items-center space-x-2"
              >
                <Printer className="w-4 h-4" />
                <span>Imprimer</span>
              </button>
              )}
              <button
                onClick={handleDownload}
                disabled={saving}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-400 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-t-2 border-white rounded-full animate-spin" />
                    <span>Generation...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span>Telecharger PDF</span>
                  </>
                )}
              </button>
              {isAdminOnly && (
              <button
                onClick={togglePreviewMode}
                disabled={saving}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                  previewMode
                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                    : 'bg-neutral-200 text-neutral-700 hover:bg-neutral-300'
                }`}
              >
                {previewMode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span>{previewMode ? 'Quitter apercu' : 'Apercu PDF'}</span>
              </button>
              )}
            {isAdminOnly && (
            <button
              onClick={handleSaveInvoice}
              disabled={saving}
              className="px-4 py-2 bg-brand-gold text-white rounded-lg hover:bg-brand-gold-light transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-t-2 border-white rounded-full animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
            )}
            <button
              onClick={onClose}
              className="text-neutral-500 hover:text-neutral-700 transition-colors p-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Barre de progression */}
          {saving && downloadProgress > 0 && (
            <div className="w-full max-w-md mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-amber-600">{downloadStatus}</span>
                <span className="text-sm font-medium text-amber-600">{downloadProgress}%</span>
              </div>
              <div className="w-full bg-neutral-200 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-amber-500 h-2.5 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${downloadProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>
        </div>

        {/* Debug Info - Visible seulement en développement */}

        {/* Preview panel when in preview mode */}
        {previewMode && (
          <div className="no-print bg-orange-50 border-b border-orange-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Scissors className="w-5 h-5 text-orange-600" />
                <h3 className="font-semibold text-orange-800">Apercu des sauts de page ({pageBreaks.length + 1} pages)</h3>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={addPageBreak}
                  className="flex items-center space-x-1 px-2 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                >
                  <Plus className="w-3 h-3" />
                  <span>Ajouter saut</span>
                </button>
                <button
                  onClick={resetPageBreaks}
                  className="flex items-center space-x-1 px-2 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                >
                  <RotateCcw className="w-3 h-3" />
                  <span>Reinitialiser</span>
                </button>
              </div>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {pageBreaks.map((pb, idx) => (
                <div key={idx} className="flex items-center space-x-3 text-sm bg-white p-2 rounded border border-orange-200">
                  <span className="bg-orange-200 text-orange-800 px-2 py-1 rounded font-mono whitespace-nowrap">
                    Page {pb.pageNumber} → {pb.pageNumber + 1}
                  </span>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => adjustPageBreak(idx, -50)}
                      className="p-1 bg-orange-100 hover:bg-orange-200 rounded"
                      title="Monter de 50px"
                    >
                      <ChevronUp className="w-4 h-4 text-orange-700" />
                    </button>
                    <button
                      onClick={() => adjustPageBreak(idx, -10)}
                      className="p-1 bg-orange-100 hover:bg-orange-200 rounded text-xs font-bold text-orange-700"
                      title="Monter de 10px"
                    >
                      -10
                    </button>
                    <span className="text-orange-700 font-mono w-20 text-center">{pb.position}px</span>
                    <button
                      onClick={() => adjustPageBreak(idx, 10)}
                      className="p-1 bg-orange-100 hover:bg-orange-200 rounded text-xs font-bold text-orange-700"
                      title="Descendre de 10px"
                    >
                      +10
                    </button>
                    <button
                      onClick={() => adjustPageBreak(idx, 50)}
                      className="p-1 bg-orange-100 hover:bg-orange-200 rounded"
                      title="Descendre de 50px"
                    >
                      <ChevronDown className="w-4 h-4 text-orange-700" />
                    </button>
                  </div>
                  <span className="text-orange-600 italic flex-1 truncate">{pb.reason}</span>
                  <button
                    onClick={() => removePageBreak(idx)}
                    className="p-1 bg-red-100 hover:bg-red-200 rounded"
                    title="Supprimer ce saut de page"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              ))}
              {pageBreaks.length === 0 && (
                <div className="text-center text-orange-600 py-4">
                  Aucun saut de page. Cliquez sur "Ajouter saut" pour en creer un.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contenu de la facture */}
        <div className="relative">
          {/* Page break indicators overlay */}
          {previewMode && pageBreaks.map((pb, idx) => (
            <div
              key={idx}
              className="absolute left-0 right-0 z-20"
              style={{ top: `${pb.position + 32}px` }}
            >
              <div className="flex items-center">
                <div className="flex-1 border-t-4 border-dashed border-red-500"></div>
                <div className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold mx-1 flex items-center space-x-1">
                  <Scissors className="w-3 h-3" />
                  <span>PAGE {pb.pageNumber + 1}</span>
                </div>
                <div className="flex items-center space-x-1 mx-1">
                  <button
                    onClick={() => adjustPageBreak(idx, -20)}
                    className="bg-white border border-red-400 hover:bg-red-100 rounded p-1"
                    title="Monter"
                  >
                    <ChevronUp className="w-3 h-3 text-red-600" />
                  </button>
                  <button
                    onClick={() => adjustPageBreak(idx, 20)}
                    className="bg-white border border-red-400 hover:bg-red-100 rounded p-1"
                    title="Descendre"
                  >
                    <ChevronDown className="w-3 h-3 text-red-600" />
                  </button>
                  <button
                    onClick={() => removePageBreak(idx)}
                    className="bg-white border border-red-400 hover:bg-red-100 rounded p-1"
                    title="Supprimer"
                  >
                    <Trash2 className="w-3 h-3 text-red-600" />
                  </button>
                </div>
                <div className="flex-1 border-t-4 border-dashed border-red-500"></div>
              </div>
            </div>
          ))}

          <div
            id="invoice-content"
            className="p-8 bg-white text-neutral-900 max-w-none"
            data-net-amount={invoiceData.calculations.netAmount.toFixed(2)}
            data-invoice-number={displayNumber}
            data-billing-ready={billingLookupDone ? 'true' : 'false'}
          >
          {(() => {
            const isPureProducerInvoice = invoiceData.calculations.netAmount < 0;

            const periodLabel = (() => {
              const start = format(parseISO(invoiceData.period.startDate), 'MMMM yyyy', { locale: fr });
              if (invoiceData.period.startMonth !== invoiceData.period.endMonth) {
                return `${start} - ${format(parseISO(invoiceData.period.endDate), 'MMMM yyyy', { locale: fr })}`;
              }
              return start;
            })();

            if (isPureProducerInvoice) {
              const producerName = isGroupInvoice
                ? ((groupParticipants.find((p: any) => (p as any).company_name) as any)?.company_name || removeGroupePrefix(participant.groupe))
                : ((invoiceData.participant as any).company_name || invoiceData.participant.name);
              const producerAddress = isGroupInvoice ? 'Adresses multiples' : invoiceData.participant.address;
              const producerEmail = invoiceData.participant.email;
              const producerCompany = invoiceData.participant.company_number;

              return (
                <>
                  {/* En-tête autoproduction : émetteur = producteur */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }} className="mb-8 pb-6 border-b-2 border-amber-300">
                    <div>
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-amber-600 uppercase tracking-widest mb-1">Émetteur</p>
                        <h1 className="text-xl font-bold text-neutral-900">{producerName}</h1>
                        {producerAddress && <p className="text-sm text-neutral-600 mt-1">{producerAddress}</p>}
                        {producerEmail && <p className="text-sm text-neutral-600">{producerEmail}</p>}
                        {producerCompany && <p className="text-sm text-neutral-600">N° entreprise : {producerCompany}</p>}
                      </div>
                      <div className="mt-4 pt-4 border-t border-neutral-200">
                        <p className="text-xs font-semibold text-neutral-500 uppercase tracking-widest mb-1">Destinataire</p>
                        <div className="flex items-center space-x-3">
                          <img
                            src={logoBase64 || '/images/Logosite2.png'}
                            alt="Sun Is Up Logo"
                            className="h-10 w-10 flex-shrink-0 rounded"
                            style={{ objectFit: 'contain' }}
                          />
                          <div>
                            <p className="font-semibold text-neutral-900">Sun Is Up ASBL</p>
                            <p className="text-xs text-neutral-600">TVA : BE 1022 108 004</p>
                            <p className="text-xs text-neutral-600">info@sunisup.be</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ display: 'block', backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '0.5rem', padding: '0.75rem 1.25rem', marginBottom: '0.75rem' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#92400e', margin: 0 }}>Décompte injection partagée</h2>
                        <p style={{ fontSize: '0.75rem', color: '#d97706', marginTop: '0.125rem', marginBottom: 0 }}>Énergie partagée — Communauté Sun Is Up</p>
                      </div>
                      <div className="text-sm text-neutral-700 space-y-1">
                        <div>Période : <span className="font-medium">{periodLabel}</span></div>
                        <div className="text-neutral-600">Facture N° {displayNumber}</div>
                        {shouldShowIssueDate && (
                          <div className="text-neutral-600">Date d'émission : {format(effectiveIssueDate, 'dd/MM/yyyy', { locale: fr })}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Objet de la facture */}
                  <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-section="invoice-subject">
                    <p className="text-sm text-amber-900">
                      <strong>Objet :</strong> Facturation de l'énergie solaire autoconsommée et partagée au sein de la communauté d'énergie Sun Is Up pour la période {periodLabel}.
                    </p>
                  </div>
                </>
              );
            }

            return (
              <>
                {/* En-tête standard : émetteur = Sun Is Up */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 pb-6 border-b-2 border-neutral-300">
                  <div>
                    <div className="flex items-center space-x-4 mb-4">
                      <img
                        src={logoBase64 || '/images/Logosite2.png'}
                        alt="Sun Is Up Logo"
                        className="h-20 w-20 flex-shrink-0 rounded-lg flex items-center justify-center"
                        style={{ objectFit: 'contain' }}
                      />
                      <div>
                        <h1 className="text-2xl font-bold text-neutral-900">Sun Is Up ASBL</h1>
                        <p className="text-neutral-700">Communauté d'énergie bruxelloise</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600 mt-2">TVA: BE 1022 108 004</p>
                      <p className="text-sm text-neutral-600">info@sunisup.be</p>
                    </div>
                  </div>
                  <div className="text-left md:text-right">
                    <h2 className="text-xl font-bold text-neutral-900 mb-2">FACTURE ÉNERGÉTIQUE</h2>
                    <div className="text-sm text-neutral-700 space-y-1">
                      <div className="flex items-center justify-start md:justify-end space-x-2">
                        <span>Période : {periodLabel}</span>
                      </div>
                      <div className="text-neutral-600">
                        Facture N° {displayNumber}
                      </div>
                      {shouldShowIssueDate && (
                        <div className="text-neutral-600">
                          Date d'émission : {format(effectiveIssueDate, 'dd/MM/yyyy', { locale: fr })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Informations du participant */}
                <div className="mb-8 p-6 bg-white rounded-lg border border-neutral-300" data-section="participant-info">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                    {isGroupInvoice ? (
                      <>
                        <Users className="w-5 h-5 mr-2 text-amber-600" />
                        Facturé à
                      </>
                    ) : (
                      <>
                        <User className="w-5 h-5 mr-2 text-amber-600" />
                        Informations du participant
                      </>
                    )}
                  </h3>
                  {(() => {
                    const companyName = companyNumberFilter !== undefined
                      ? (groupParticipants.find(p => (p as any).company_name) as any)?.company_name || null
                      : (invoiceData.participant as any).company_name || null;
                    const companyNumber = companyNumberFilter !== undefined
                      ? companyNumberFilter
                      : invoiceData.participant.company_number;
                    const displayName = isGroupInvoice
                      ? (companyName || removeGroupePrefix(participant.groupe))
                      : invoiceData.participant.name;
                    return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-neutral-800">
                    <div>
                      <div className="space-y-2">
                        <div><strong className="text-neutral-900">Nom :</strong> {displayName}</div>
                        {companyName && companyName !== displayName && (
                          <div><strong className="text-neutral-900">Dénomination :</strong> {companyName}</div>
                        )}
                        <div><strong className="text-neutral-900">Adresse :</strong> {isGroupInvoice ? 'Adresses multiples' : invoiceData.participant.address}</div>
                        {invoiceData.participant.email && (
                          <div><strong className="text-neutral-900">Email :</strong> {invoiceData.participant.email}</div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="space-y-2">
                        <div><strong className="text-neutral-900">Code EAN :</strong> {isGroupInvoice ? 'Multiples codes EAN' : invoiceData.participant.ean_code}</div>
                        <div>
                          <strong className="text-neutral-900">Type :</strong>
                          <span className="ml-2">{invoiceData.participant.type === 'producer' ? 'Producteur' : invoiceData.participant.type === 'prosumer' ? 'Prosumer' : 'Consommateur'}</span>
                        </div>
                        {companyNumber && (
                          <div><strong className="text-neutral-900">N° entreprise :</strong> {companyNumber}</div>
                        )}
                      </div>
                    </div>
                  </div>
                    );
                  })()}
                </div>
              </>
            );
          })()}

          {/* Détail énergétique */}
          <div className="mb-8" data-section="energy-detail">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Détail énergétique</h3>

            {/* Affichage détaillé pour les groupes - MÊME LOGIQUE QUE LE DASHBOARD */}
            {isGroupInvoice && groupParticipants && groupParticipants.length > 0 && (() => {
              const isProducerOnly = groupParticipants.every((p: any) => p.type === 'producer');

              return (
              <div className="mb-6" data-section="group-participants-table">
                <h4 className="text-md font-semibold text-neutral-800 mb-3">
                  Détail par participant ({groupParticipants.length} membres)
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Participant
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Type
                        </th>
                        {!isProducerOnly && (
                          <>
                            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                              Cons. Partagée
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                              Cons. Réseau
                            </th>
                          </>
                        )}
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Inj. Partagée
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Inj. Réseau
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {groupParticipants.map((groupParticipant: any) => {
                        // EXACTEMENT LA MÊME LOGIQUE QUE LE DASHBOARD
                        // Extraire l'année de la période de facturation
                        const invoiceYear = selectedPeriod.startMonth.split('-')[0];

                        // Calculer les données individuelles pour ce participant
                        const individualMonthlyData = processIndividualMonthlyData(
                          groupParticipant,
                          generateMonthsInPeriod(selectedPeriod.startMonth, selectedPeriod.endMonth)
                        );

                        // Calculer displayData basé sur la période
                        // Si période = 1 mois, afficher ce mois
                        // Si période > 1 mois, faire la somme (équivalent à 'total')
                        const displayData = individualMonthlyData.reduce((totals, month) => ({
                          volume_partage: totals.volume_partage + month.volume_partage,
                          volume_complementaire: totals.volume_complementaire + month.volume_complementaire,
                          injection_partagee: totals.injection_partagee + month.injection_partagee,
                          injection_complementaire: totals.injection_complementaire + month.injection_complementaire
                        }), {
                          volume_partage: 0,
                          volume_complementaire: 0,
                          injection_partagee: 0,
                          injection_complementaire: 0
                        });

                        return (
                          <tr key={groupParticipant.id} className="hover:bg-white" data-participant-row="true">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="ml-4">
                                  <div className="flex items-center space-x-2">
                                    <div className="text-sm font-medium text-neutral-900">
                                      {groupParticipant.name}
                                    </div>
                                  </div>
                                  {groupParticipant.ean_code && (
                                    <div className="text-xs text-neutral-500 font-mono">
                                      {groupParticipant.ean_code}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                groupParticipant.type === 'producer'
                                  ? 'bg-amber-100 text-amber-800'
                                  : groupParticipant.type === 'prosumer'
                                  ? 'bg-teal-100 text-teal-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {groupParticipant.type === 'producer' ? 'Producteur' : groupParticipant.type === 'prosumer' ? 'Prosumer' : 'Consommateur'}
                              </span>
                            </td>
                            {!isProducerOnly && (
                              <>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                                  {(displayData.volume_partage / 1000).toFixed(2)} MWh
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                                  {(displayData.volume_complementaire / 1000).toFixed(2)} MWh
                                </td>
                              </>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                              {(displayData.injection_partagee / 1000).toFixed(2)} MWh
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                              {(displayData.injection_complementaire / 1000).toFixed(2)} MWh
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              );
            })()}
            
            {(() => {
              const isProducerOnly = isGroupInvoice
                ? groupParticipants.every((p: any) => p.type === 'producer')
                : participant.type === 'producer';

              return (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Consommation - hide for producer-only */}
                  {!isProducerOnly && (
                    <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
                      <h4 className="font-medium text-amber-600 mb-3">Consommation</h4>
                      <div className="space-y-2 text-sm text-amber-600">
                        <div className="flex justify-between">
                          <span>Énergie partagée :</span>
                          <span className="font-medium">
                            {(() => {
                              // Pour les groupes, calculer depuis le tableau des participants
                              if (isGroupInvoice && groupParticipants && groupParticipants.length > 0) {
                                const periodMonths = generateMonthsInPeriod(selectedPeriod.startMonth, selectedPeriod.endMonth);
                                const total = groupParticipants.reduce((sum, member) => {
                                  const individualMonthlyData = processIndividualMonthlyData(member, periodMonths);
                                  const memberTotal = individualMonthlyData.reduce((acc, month) => acc + month.volume_partage, 0);
                                  return sum + memberTotal;
                                }, 0);
                                return (total / 1000).toFixed(3);
                              }
                              // Pour les participants individuels, utiliser les totaux existants
                              return (invoiceData.totals.volume_partage / 1000).toFixed(3);
                            })()} MWh
                          </span>
                        </div>
                        <div className="flex justify-between border-t border-yellow-200 pt-2 mt-2">
                          <span>Énergie réseau (achat au fournisseur) :</span>
                          <span className="font-medium text-amber-600">
                            {(() => {
                              // Pour les groupes, calculer depuis le tableau des participants
                              if (isGroupInvoice && groupParticipants && groupParticipants.length > 0) {
                                const periodMonths = generateMonthsInPeriod(selectedPeriod.startMonth, selectedPeriod.endMonth);
                                const total = groupParticipants.reduce((sum, member) => {
                                  const individualMonthlyData = processIndividualMonthlyData(member, periodMonths);
                                  const memberTotal = individualMonthlyData.reduce((acc, month) => acc + month.volume_complementaire, 0);
                                  return sum + memberTotal;
                                }, 0);
                                return (total / 1000).toFixed(3);
                              }
                              return (invoiceData.totals.volume_complementaire / 1000).toFixed(3);
                            })()} MWh
                          </span>
                        </div>
                        <div className="text-xs text-neutral-600 italic mt-2">
                          * Énergie réseau : indicatif, non facturée par Sun Is Up
                        </div>
                      </div>
                    </div>
                  )}

              {/* Injection - afficher uniquement s'il y a de l'injection */}
              {(() => {
                let totalInjection = 0;
                if (isGroupInvoice && groupParticipants && groupParticipants.length > 0) {
                  const periodMonths = generateMonthsInPeriod(selectedPeriod.startMonth, selectedPeriod.endMonth);
                  totalInjection = groupParticipants.reduce((sum, member) => {
                    const individualMonthlyData = processIndividualMonthlyData(member, periodMonths);
                    const memberTotal = individualMonthlyData.reduce((acc, month) =>
                      acc + month.injection_partagee + month.injection_complementaire, 0);
                    return sum + memberTotal;
                  }, 0);
                } else {
                  totalInjection = (invoiceData.totals.injection_partagee || 0) + (invoiceData.totals.injection_complementaire || 0);
                }

                if (totalInjection > 0) {
                  return (
                    <div className="bg-brand-gold/10 p-6 rounded-lg border border-brand-gold/30">
                      <h4 className="font-medium text-amber-900 mb-3">Injection</h4>
                      <div className="space-y-2 text-sm text-amber-800">
                        <div className="flex justify-between">
                          <span>Injection partagée :</span>
                          <span className="font-medium">
                            {(() => {
                              if (isGroupInvoice && groupParticipants && groupParticipants.length > 0) {
                                const periodMonths = generateMonthsInPeriod(selectedPeriod.startMonth, selectedPeriod.endMonth);
                                const total = groupParticipants.reduce((sum, member) => {
                                  const individualMonthlyData = processIndividualMonthlyData(member, periodMonths);
                                  const memberTotal = individualMonthlyData.reduce((acc, month) => acc + month.injection_partagee, 0);
                                  return sum + memberTotal;
                                }, 0);
                                return (total / 1000).toFixed(3);
                              }
                              return ((invoiceData.totals.injection_partagee || 0) / 1000).toFixed(3);
                            })()} MWh
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Injection réseau :</span>
                          <span className="font-medium">
                            {(() => {
                              if (isGroupInvoice && groupParticipants && groupParticipants.length > 0) {
                                const periodMonths = generateMonthsInPeriod(selectedPeriod.startMonth, selectedPeriod.endMonth);
                                const total = groupParticipants.reduce((sum, member) => {
                                  const individualMonthlyData = processIndividualMonthlyData(member, periodMonths);
                                  const memberTotal = individualMonthlyData.reduce((acc, month) => acc + month.injection_complementaire, 0);
                                  return sum + memberTotal;
                                }, 0);
                                return (total / 1000).toFixed(3);
                              }
                              return ((invoiceData.totals.injection_complementaire || 0) / 1000).toFixed(3);
                            })()} MWh
                          </span>
                        </div>
                        <div className="flex justify-between border-t border-amber-300 pt-2 mt-2 font-semibold text-amber-900 bg-amber-100 p-2 rounded">
                          <span>Total injection :</span>
                          <span>
                            {(totalInjection / 1000).toFixed(3)} MWh
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
                </div>
              );
            })()}
          </div>

          {/* Détail des coûts réseau - Hide for producer-only and autofacturation */}
          {(() => {
            const isProducerOnly = isGroupInvoice
              ? groupParticipants.every((p: any) => p.type === 'producer')
              : participant.type === 'producer';

            const isAutofacturation = invoiceData.calculations.netAmount < 0;

            if (isProducerOnly || isAutofacturation) return null;

            return (
              <div className="mb-8" data-section="network-costs">
                <h3 className="text-lg font-semibold text-neutral-900 mb-4">Détail des coûts réseau</h3>

            <div className="overflow-x-auto border border-neutral-300 rounded-lg">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-neutral-50 border-b border-neutral-300">
                    <th className="px-4 py-3 text-left text-sm font-bold text-neutral-900 border-r border-neutral-300">
                      Description
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-neutral-900 border-r border-neutral-300">
                      Taux TVA
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">
                      Montant HTVA
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900">
                      Montant TVAC
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr className="border-b border-neutral-300">
                    <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                      <div>
                        <div className="font-medium text-neutral-900">Utilisation du réseau</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">
                      {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">
                      {invoiceData.totals.networkCosts.utilisationReseau.toFixed(2)} €
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                      {(invoiceData.totals.networkCosts.utilisationReseau * (1 + invoiceData.calculations.vatRate)).toFixed(2)} €
                    </td>
                  </tr>
                  <tr className="border-b border-neutral-300">
                    <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                      <div>
                        <div className="font-medium text-neutral-900">Surcharges</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">
                      {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">
                      {invoiceData.totals.networkCosts.surcharges.toFixed(2)} €
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                      {(invoiceData.totals.networkCosts.surcharges * (1 + invoiceData.calculations.vatRate)).toFixed(2)} €
                    </td>
                  </tr>
                  <tr className="border-b border-neutral-300">
                    <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                      <div>
                        <div className="font-medium text-neutral-900">Tarif capacitaire</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">
                      {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">
                      {invoiceData.totals.networkCosts.tarifCapacitaire.toFixed(2)} €
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                      {(invoiceData.totals.networkCosts.tarifCapacitaire * (1 + invoiceData.calculations.vatRate)).toFixed(2)} €
                    </td>
                  </tr>
                  <tr className="border-b border-neutral-300">
                    <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                      <div>
                        <div className="font-medium text-neutral-900">Tarif mesure & comptage</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">
                      {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">
                      {invoiceData.totals.networkCosts.tarifMesure.toFixed(2)} €
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                      {(invoiceData.totals.networkCosts.tarifMesure * (1 + invoiceData.calculations.vatRate)).toFixed(2)} €
                    </td>
                  </tr>
                  <tr className="border-b border-neutral-300">
                    <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                      <div>
                        <div className="font-medium text-neutral-900">Tarif OSP</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">
                      {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">
                      {invoiceData.totals.networkCosts.tarifOSP.toFixed(2)} €
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                      {(invoiceData.totals.networkCosts.tarifOSP * (1 + invoiceData.calculations.vatRate)).toFixed(2)} €
                    </td>
                  </tr>
                  <tr className="border-b border-neutral-300">
                    <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                      <div>
                        <div className="font-medium text-neutral-900">Transport ELIA</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">
                      {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">
                      {invoiceData.totals.networkCosts.transportELIA.toFixed(2)} €
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                      {(invoiceData.totals.networkCosts.transportELIA * (1 + invoiceData.calculations.vatRate)).toFixed(2)} €
                    </td>
                  </tr>
                  <tr className="border-b border-neutral-300">
                    <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                      <div>
                        <div className="font-medium text-neutral-900">Redevance voirie</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">
                      {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">
                      {invoiceData.totals.networkCosts.redevanceVoirie.toFixed(2)} €
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                      {(invoiceData.totals.networkCosts.redevanceVoirie * (1 + invoiceData.calculations.vatRate)).toFixed(2)} €
                    </td>
                  </tr>
                  <tr className="bg-neutral-50 border-t-2 border-neutral-300">
                    <td className="px-4 py-3 text-sm font-bold text-neutral-900 border-r border-neutral-300">
                      TOTAL FRAIS RÉSEAU
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-bold text-neutral-900 border-r border-neutral-300">
                      {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">
                      {invoiceData.totals.networkCosts.totalFraisReseau.toFixed(2)} €
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-neutral-900">
                      {(invoiceData.totals.networkCosts.totalFraisReseau * (1 + invoiceData.calculations.vatRate)).toFixed(2)} €
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
              </div>
            );
          })()}

          {/* Droit d'accise et cotisation sur l'énergie */}
          {(() => {
            const isProducerOnly = isGroupInvoice
              ? groupParticipants.every((p: any) => p.type === 'producer')
              : participant.type === 'producer';

            if (isProducerOnly) return null;

            return (
              <div className="mb-8" data-section="excise-costs">
                <h3 className="text-lg font-semibold text-neutral-900 mb-4">Droit d'accise et cotisation sur l'énergie</h3>

                <div className="overflow-x-auto border border-neutral-300 rounded-lg">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-neutral-50 border-b border-neutral-300">
                        <th className="px-4 py-3 text-left text-sm font-bold text-neutral-900 border-r border-neutral-300">
                          Description
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-bold text-neutral-900 border-r border-neutral-300">
                          Taux TVA
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">
                          Montant HTVA
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900">
                          Montant TVAC
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="border-b border-neutral-300">
                        <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                          <div>
                            <div className="font-medium text-neutral-900">
                              {isAnnualRegularization ? 'Régularisation droit d\'accise spécial' : 'Droit d\'accise spécial'}
                            </div>
                            <div className="text-xs text-neutral-600">
                              {isAnnualRegularization ? (
                                <>
                                  {(() => {
                                    const year = invoiceData.period.startMonth.split('-')[0];
                                    const annualVol = calculateAnnualVolume(invoiceData.participant, year, groupParticipants);
                                    return (annualVol * 1000).toFixed(0);
                                  })()} kWh (volume {invoiceData.period.startMonth.split('-')[0]}) × {
                                    (() => {
                                      const annualConsumption = Number(invoiceData.participant.annual_consumption || 30);
                                      const isCompany = !!invoiceData.participant.company_number;
                                      let rate = 0;
                                      if (isCompany) {
                                        if (annualConsumption <= 20) rate = billingConfig.accise_ent_t1 ?? 14.21;
                                        else if (annualConsumption <= 50) rate = billingConfig.accise_ent_t2 ?? 12.09;
                                        else if (annualConsumption <= 1000) rate = billingConfig.accise_ent_t3 ?? 11.39;
                                        else rate = billingConfig.accise_ent_t4 ?? 10.69;
                                      } else {
                                        if (annualConsumption <= 20) rate = billingConfig.accise_part_t1 ?? 47.48;
                                        else if (annualConsumption <= 50) rate = billingConfig.accise_part_t2 ?? 45.46;
                                        else if (annualConsumption <= 1000) rate = billingConfig.accise_part_t3 ?? 44.78;
                                        else rate = billingConfig.accise_part_t4 ?? 44.11;
                                      }
                                      return rate.toFixed(4);
                                    })()
                                  } EUR/MWh
                                </>
                              ) : (
                                <>
                                  {invoiceData.totals.volume_partage.toFixed(0)} kWh × {
                                    (() => {
                                      const annualConsumption = Number(invoiceData.participant.annual_consumption || 30);
                                      const isCompany = !!invoiceData.participant.company_number;
                                      let rate = 0;
                                      if (isCompany) {
                                        if (annualConsumption <= 20) rate = billingConfig.accise_ent_t1 ?? 14.21;
                                        else if (annualConsumption <= 50) rate = billingConfig.accise_ent_t2 ?? 12.09;
                                        else if (annualConsumption <= 1000) rate = billingConfig.accise_ent_t3 ?? 11.39;
                                        else rate = billingConfig.accise_ent_t4 ?? 10.69;
                                      } else {
                                        if (annualConsumption <= 20) rate = billingConfig.accise_part_t1 ?? 47.48;
                                        else if (annualConsumption <= 50) rate = billingConfig.accise_part_t2 ?? 45.46;
                                        else if (annualConsumption <= 1000) rate = billingConfig.accise_part_t3 ?? 44.78;
                                        else rate = billingConfig.accise_part_t4 ?? 44.11;
                                      }
                                      return rate.toFixed(4);
                                    })()
                                  } EUR/MWh
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">
                          {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">
                          {invoiceData.calculations.exciseCosts.droitAccise.toFixed(2)} €
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                          {(invoiceData.calculations.exciseCosts.droitAccise * (1 + invoiceData.calculations.vatRate)).toFixed(2)} €
                        </td>
                      </tr>
                      <tr className="border-b border-neutral-300">
                        <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                          <div>
                            <div className="font-medium text-neutral-900">
                              {isAnnualRegularization ? 'Régularisation cotisation sur l\'énergie' : 'Cotisation sur l\'énergie'}
                            </div>
                            <div className="text-xs text-neutral-600">
                              {isAnnualRegularization ? (
                                <>
                                  {(() => {
                                    const year = invoiceData.period.startMonth.split('-')[0];
                                    const annualVol = calculateAnnualVolume(invoiceData.participant, year, groupParticipants);
                                    return (annualVol * 1000).toFixed(0);
                                  })()} kWh (volume {invoiceData.period.startMonth.split('-')[0]}) × {(billingConfig.cotisation_energie ?? 1.9261).toFixed(4)} EUR/MWh
                                </>
                              ) : (
                                <>
                                  {invoiceData.totals.volume_partage.toFixed(0)} kWh × {(billingConfig.cotisation_energie ?? 1.9261).toFixed(4)} EUR/MWh
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">
                          {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">
                          {invoiceData.calculations.exciseCosts.cotisationEnergie.toFixed(2)} €
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                          {(invoiceData.calculations.exciseCosts.cotisationEnergie * (1 + invoiceData.calculations.vatRate)).toFixed(2)} €
                        </td>
                      </tr>
                      <tr className="bg-neutral-50 border-t-2 border-neutral-300">
                        <td className="px-4 py-3 text-sm font-bold text-neutral-900 border-r border-neutral-300">
                          TOTAL ACCISES
                        </td>
                        <td className="px-4 py-3 text-center text-sm font-bold text-neutral-900 border-r border-neutral-300">
                          {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">
                          {invoiceData.calculations.exciseCosts.totalExcise.toFixed(2)} €
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-neutral-900">
                          {invoiceData.calculations.exciseCosts.totalExciseTVAC.toFixed(2)} €
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Récapitulatif financier */}
          <div className="mb-8" data-section="financial-summary">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">
              Récapitulatif financier
            </h3>
            <div className="overflow-x-auto border border-neutral-300 rounded-lg">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-neutral-50 border-b border-neutral-300">
                    <th className="px-4 py-3 text-left text-sm font-bold text-neutral-900 border-r border-neutral-300">
                      Description
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-bold text-neutral-900 border-r border-neutral-300">
                      Taux TVA
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">
                      Montant HTVA
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900">
                      Montant TVAC
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoiceData.calculations.energySharedCostHTVA !== 0 && (
                  <tr className="border-b border-neutral-300">
                    <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                      <div>
                        <div className="font-medium text-neutral-900">Énergie partagée</div>
                        <div className="text-xs text-neutral-600">
                          {(() => {
                            if (isGroupInvoice && groupParticipants && groupParticipants.length > 0) {
                              const periodMonths = generateMonthsInPeriod(selectedPeriod.startMonth, selectedPeriod.endMonth);
                              const total = groupParticipants.reduce((sum, member) => {
                                const individualMonthlyData = processIndividualMonthlyData(member, periodMonths);
                                const memberTotal = individualMonthlyData.reduce((acc, month) => acc + month.volume_partage, 0);
                                return sum + memberTotal;
                              }, 0);
                              return (total / 1000).toFixed(3);
                            }
                            return (invoiceData.totals.volume_partage / 1000).toFixed(3);
                          })()} MWh × {(() => {
                            const p = invoiceData.participant as any;
                            if (invoiceData.calculations.averageBuyPrice != null) {
                              return `${invoiceData.calculations.averageBuyPrice}€/MWh HTVA (achat)`;
                            }
                            if (p.shared_energy_formula_type === 'variable' && p.shared_energy_a) {
                              return `${p.shared_energy_a} x ${p.shared_energy_x_param || 'X'}`;
                            }
                            return `${resolvePriceForMonth((invoiceData.participant as any).shared_energy_price ?? null, (invoiceData.participant as any)._tariffOverrides || [], selectedPeriod.startMonth)}€/MWh`;
                          })()}{(invoiceData.participant as any).type !== 'prosumer' && (invoiceData.participant as any).shared_energy_formula_type !== 'variable' ? ' HTVA' : ''}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">
                      {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">
                      {invoiceData.calculations.energySharedCostHTVA.toFixed(2)} €
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                      {invoiceData.calculations.energySharedCostTVAC.toFixed(2)} €
                    </td>
                  </tr>
                  )}
                  {invoiceData.calculations.networkCostTotal !== 0 && (
                  <tr className="border-b border-neutral-300">
                    <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                      <div>
                        <div className="font-medium text-neutral-900">Coûts réseau</div>
                        <div className="text-xs text-neutral-600">Frais remboursés à Sibelga</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">
                      {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">
                      {invoiceData.calculations.networkCostTotal.toFixed(2)} €
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                      {invoiceData.calculations.networkCostTVAC.toFixed(2)} €
                    </td>
                  </tr>
                  )}
                  {(() => {
                    const isProducerOnly = isGroupInvoice
                      ? groupParticipants.every((p: any) => p.type === 'producer')
                      : participant.type === 'producer';

                    if (isProducerOnly) return null;

                    return (
                      <tr className="border-b border-neutral-300">
                        <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                          <div>
                            <div className="font-medium text-neutral-900">
                              {isAnnualRegularization ? 'Régularisation accises et cotisation énergie' : 'Accises et cotisation énergie'}
                            </div>
                            <div className="text-xs text-neutral-600">
                              {isAnnualRegularization ? `Calculé sur base des volumes partagés en ${invoiceData.period.startMonth.split('-')[0]}` : 'Frais remboursés au gouvernement fédéral'}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">
                          {(invoiceData.calculations.vatRate * 100).toFixed(0)}%
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">
                          {invoiceData.calculations.exciseCosts.totalExcise.toFixed(2)} €
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                          {invoiceData.calculations.exciseCosts.totalExciseTVAC.toFixed(2)} €
                        </td>
                      </tr>
                    );
                  })()}
                  {invoiceData.calculations.isFirstInvoiceOfYear && (
                    <tr className="border-b border-neutral-300">
                      <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                        <div>
                          <div className="font-medium text-neutral-900">Frais d'adhésion annuels</div>
                          <div className="text-xs text-neutral-600">
                            {invoiceData.calculations.membershipFeeYear
                              ? `Cotisation membre ${invoiceData.calculations.membershipFeeYear}-${invoiceData.calculations.membershipFeeYear + 1}`
                              : 'Cotisation membre annuelle'}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">{(invoiceData.calculations.vatRate * 100).toFixed(0)}%</td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">
                        {invoiceData.calculations.membershipFeeHTVA.toFixed(2)} €
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                        {invoiceData.calculations.membershipFeeTVAC.toFixed(2)} €
                      </td>
                    </tr>
                  )}
                  <tr className="border-b-2 border-neutral-300 bg-yellow-50 font-semibold">
                    <td className="px-4 py-3 text-sm font-bold text-amber-600 border-r border-neutral-300">
                      SOUS-TOTAL COÛTS
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-bold text-amber-600 border-r border-neutral-300">-</td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-amber-600 border-r border-neutral-300">
                      {(
                        invoiceData.calculations.energySharedCostHTVA +
                        invoiceData.calculations.networkCostTotal +
                        invoiceData.calculations.exciseCosts.totalExcise +
                        (invoiceData.calculations.membershipFeeHTVA || 0)
                      ).toFixed(2)} €
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-amber-600">
                      {invoiceData.calculations.totalCostTVAC.toFixed(2)} €
                    </td>
                  </tr>
                  {invoiceData.calculations.injectionRevenue > 0 && (
                    <tr className="border-b border-neutral-300">
                      <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                        <div>
                          <div className="font-medium text-neutral-900">Revenus injection</div>
                          <div className="text-xs text-neutral-600">
                            {(() => {
                              // Pour les groupes, calculer depuis le tableau des participants - INJECTION PARTAGÉE UNIQUEMENT
                              if (isGroupInvoice && groupParticipants && groupParticipants.length > 0) {
                                const periodMonths = generateMonthsInPeriod(selectedPeriod.startMonth, selectedPeriod.endMonth);
                                const totalInjectionPartagee = groupParticipants.reduce((sum, member) => {
                                  const individualMonthlyData = processIndividualMonthlyData(member, periodMonths);
                                  const memberTotal = individualMonthlyData.reduce((acc, month) => acc + month.injection_partagee, 0);
                                  return sum + memberTotal;
                                }, 0);
                                return (totalInjectionPartagee / 1000).toFixed(3);
                              }
                              return ((invoiceData.totals.injection_partagee || 0) / 1000).toFixed(3);
                            })()} MWh × {(() => {
                              const p = invoiceData.participant as any;
                              if (invoiceData.calculations.averageSellPrice != null) {
                                return `${invoiceData.calculations.averageSellPrice}€/MWh (vente)`;
                              }
                              return `${resolvePriceForMonth(p.shared_energy_price ?? null, p._tariffOverrides || [], selectedPeriod.startMonth)}€/MWh`;
                            })()}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-neutral-900 border-r border-neutral-300">
                        {((invoiceData.calculations.injectionVatRate ?? invoiceData.calculations.vatRate) * 100).toFixed(0)}%
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-amber-600 border-r border-neutral-300">
                        -{invoiceData.calculations.injectionRevenue.toFixed(2)} €
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-amber-600">
                        -{(invoiceData.calculations.injectionRevenue * (1 + (invoiceData.calculations.injectionVatRate ?? invoiceData.calculations.vatRate))).toFixed(2)} €
                      </td>
                    </tr>
                  )}
                  <tr className="bg-amber-100 border-t-2 border-amber-400">
                    <td className="px-4 py-3 text-lg font-bold text-amber-900 border-r border-neutral-300">
                      {invoiceData.calculations.netAmount < 0 ? 'MONTANT NET DÛ AU PRODUCTEUR' : 'MONTANT NET À PAYER'}
                    </td>
                    <td className="px-4 py-3 text-center text-lg font-bold text-amber-900 border-r border-neutral-300">-</td>
                    <td className="px-4 py-3 text-right text-lg font-bold text-amber-900 border-r border-neutral-300">-</td>
                    <td className="px-4 py-3 text-right text-lg font-bold text-amber-900">
                      {Math.abs(invoiceData.calculations.netAmount).toFixed(2)} €
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Annexe frais réseau par participant (groupes uniquement, hors producteurs) */}
          {isGroupInvoice && invoiceData.calculations.netAmount >= 0 && (() => {
            const nonProducerDetails = participantNetworkDetails.filter(p => p.type !== 'producer');
            if (nonProducerDetails.length === 0) return null;
            const annexTotals = nonProducerDetails.reduce((acc, p) => {
              acc.utilisationReseau += p.networkCosts.utilisationReseau;
              acc.surcharges += p.networkCosts.surcharges;
              acc.tarifCapacitaire += p.networkCosts.tarifCapacitaire;
              acc.tarifMesure += p.networkCosts.tarifMesure;
              acc.tarifOSP += p.networkCosts.tarifOSP;
              acc.transportELIA += p.networkCosts.transportELIA;
              acc.redevanceVoirie += p.networkCosts.redevanceVoirie;
              acc.totalFraisReseau += p.networkCosts.totalFraisReseau;
              return acc;
            }, { utilisationReseau: 0, surcharges: 0, tarifCapacitaire: 0, tarifMesure: 0, tarifOSP: 0, transportELIA: 0, redevanceVoirie: 0, totalFraisReseau: 0 });
            return (
            <div className="mb-8" data-section="annex-network-costs">
              <h3 className="text-lg font-semibold text-neutral-900 mb-1">Annexe — Frais de réseau par participant</h3>
              <p className="text-sm text-neutral-500 mb-4">Détail des frais de réseau individuels pour la période facturée (montants HTVA) — consommateurs et prosumers uniquement</p>
              <div className="overflow-x-auto border border-neutral-300 rounded-lg">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-neutral-50 border-b border-neutral-300">
                      <th className="px-4 py-3 text-left text-sm font-bold text-neutral-900 border-r border-neutral-300">Participant</th>
                      <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">Util. réseau</th>
                      <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">Surcharges</th>
                      <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">Capacitaire</th>
                      <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">Mesure</th>
                      <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">OSP</th>
                      <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">Transport ELIA</th>
                      <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">Voirie</th>
                      <th className="px-4 py-3 text-right text-sm font-bold text-neutral-900">Total HTVA</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {nonProducerDetails.map((p, idx) => (
                      <tr key={idx} className="border-b border-neutral-300">
                        <td className="px-4 py-3 text-sm text-neutral-900 border-r border-neutral-300">
                          <div className="font-medium text-neutral-900">{p.name}</div>
                          {p.ean_code && <div className="text-xs text-neutral-500">{p.ean_code}</div>}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">{p.networkCosts.utilisationReseau.toFixed(2)} €</td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">{p.networkCosts.surcharges.toFixed(2)} €</td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">{p.networkCosts.tarifCapacitaire.toFixed(2)} €</td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">{p.networkCosts.tarifMesure.toFixed(2)} €</td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">{p.networkCosts.tarifOSP.toFixed(2)} €</td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">{p.networkCosts.transportELIA.toFixed(2)} €</td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900 border-r border-neutral-300">{p.networkCosts.redevanceVoirie.toFixed(2)} €</td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">{p.networkCosts.totalFraisReseau.toFixed(2)} €</td>
                      </tr>
                    ))}
                    <tr className="bg-neutral-50 border-t-2 border-neutral-300">
                      <td className="px-4 py-3 text-sm font-bold text-neutral-900 border-r border-neutral-300">TOTAL FRAIS RÉSEAU</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">{annexTotals.utilisationReseau.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">{annexTotals.surcharges.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">{annexTotals.tarifCapacitaire.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">{annexTotals.tarifMesure.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">{annexTotals.tarifOSP.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">{annexTotals.transportELIA.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-neutral-900 border-r border-neutral-300">{annexTotals.redevanceVoirie.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-neutral-900">{annexTotals.totalFraisReseau.toFixed(2)} €</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            );
          })()}

          {/* Conditions de paiement */}
          {(() => {
            const isPureProducerPayment = invoiceData.calculations.netAmount < 0;

            if (isPureProducerPayment) {
              return (
                <div className="mt-8 p-4 bg-amber-50 rounded-lg border-2 border-amber-200" data-section="payment-terms">
                  <h4 className="font-semibold text-neutral-900 mb-2">Modalités de règlement</h4>
                  <div className="text-sm text-neutral-700 space-y-1">
                    <p>• Paiement par Sun Is Up ASBL dans les 30 jours, soit au plus tard le {shouldShowIssueDate ? format(addDays(effectiveIssueDate, 30), 'dd/MM/yyyy', { locale: fr }) : '—'}{!shouldShowIssueDate && ' (date à définir)'}</p>
                    <p>• Virement bancaire vers le compte du producteur</p>
                    <p>• Communication : {displayNumber}</p>
                    <p className="mt-2 text-xs text-neutral-500 italic">
                      Merci de communiquer vos coordonnées bancaires à info@sunisup.be si ce n'est pas encore fait.
                    </p>
                  </div>
                </div>
              );
            }

            if (directDebit) {
              return (
                <div className="mt-8 p-4 bg-neutral-50 rounded-lg border-2 border-neutral-300" data-section="payment-terms">
                  <h4 className="font-semibold text-neutral-900 mb-2">Conditions de paiement</h4>
                  <div className="text-sm text-neutral-700 space-y-1">
                    <p>• Paiement par domiciliation SEPA : prélèvement le <span className="font-semibold">{format(parseISO(String(directDebit.collectionDate)), 'dd/MM/yyyy', { locale: fr })}</span></p>
                    {directDebit.maskedIban && <p>• Compte débité : {directDebit.maskedIban}</p>}
                    <p>• Mandat : {directDebit.mandateReference ?? '—'}{directDebit.creditorId ? ` · Identifiant créancier : ${directDebit.creditorId}` : ''}</p>
                    <p>• Référence : {displayNumber}</p>
                    <p className="mt-2 text-xs text-neutral-500 italic">Aucun virement à effectuer : le montant sera prélevé automatiquement à cette date.</p>
                  </div>
                </div>
              );
            }

            return (
              <div className="mt-8 p-4 bg-neutral-50 rounded-lg border-2 border-neutral-300" data-section="payment-terms">
                <h4 className="font-semibold text-neutral-900 mb-2">Conditions de paiement</h4>
                <div className="text-sm text-neutral-700 space-y-1">
                  <p>• Paiement à 30 jours{shouldShowIssueDate ? `, soit au plus tard le ${format(addDays(effectiveIssueDate, 30), 'dd/MM/yyyy', { locale: fr })}` : ' (échéance à définir)'}</p>
                  <p>• Virement bancaire : BE96 0020 1192 6005</p>
                  <p>• Communication : {displayNumber}</p>
                </div>
              </div>
            );
          })()}
        </div>
        </div>
      </div>
    </div>
  );
}

export async function generateInvoicePDF(
  participant: Participant,
  selectedPeriod: { startMonth: string; endMonth: string },
  includeMembershipFee?: boolean,
  isAnnualRegularization?: boolean,
  companyNumberFilter?: string | null,
  issueDate?: string | null
): Promise<Blob | null> {
  const result = await generateInvoicePDFWithMeta(participant, selectedPeriod, includeMembershipFee, isAnnualRegularization, companyNumberFilter, issueDate);
  return result?.blob ?? null;
}

/** PDF de la facture, avec le montant net à payer et le numéro tels qu'imprimés (lus dans la page générée). */
export interface GeneratedInvoice {
  blob: Blob;
  /** Montant net à payer (négatif : décompte d'un producteur), arrondi au centime comme sur le PDF. */
  netAmount: number;
  invoiceNumber: string;
}

export async function generateInvoicePDFWithMeta(
  participant: Participant,
  selectedPeriod: { startMonth: string; endMonth: string },
  includeMembershipFee?: boolean,
  isAnnualRegularization?: boolean,
  companyNumberFilter?: string | null,
  issueDate?: string | null,
  billingInfo?: InvoiceBillingInfo | null
): Promise<GeneratedInvoice | null> {
  try {
    const React = await import('react');
    const ReactDOM = await import('react-dom/client');
    const html2canvas = (await import('html2canvas')).default;
    const jsPDF = (await import('jspdf')).default;

    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'fixed';
    tempContainer.style.left = '0';
    tempContainer.style.top = '0';
    tempContainer.style.width = '1200px';
    tempContainer.style.height = 'auto';
    tempContainer.style.zIndex = '-9999';
    tempContainer.style.opacity = '0';
    tempContainer.style.pointerEvents = 'none';
    tempContainer.style.overflow = 'visible';
    document.body.appendChild(tempContainer);

    const root = ReactDOM.createRoot(tempContainer);
    root.render(React.createElement(InvoiceTemplate, {
      isOpen: true,
      onClose: () => {},
      participant,
      selectedPeriod,
      includeMembershipFee,
      isAnnualRegularization,
      companyNumberFilter,
      issueDate,
      billingInfo,
    }));

    // Poll DOM until #invoice-content appears and is stable (max 30s)
    let invoiceElement: HTMLElement | null = null;
    const pollStart = Date.now();
    while (!invoiceElement && Date.now() - pollStart < 30000) {
      await new Promise(r => setTimeout(r, 500));
      invoiceElement = tempContainer.querySelector('#invoice-content') as HTMLElement | null;
    }

    if (!invoiceElement) {
      console.error('generateInvoicePDF: #invoice-content not found after 30s for', participant.name, companyNumberFilter);
      root.unmount();
      document.body.removeChild(tempContainer);
      return null;
    }

    // Wait for content to stabilize (second loadInvoiceData may be running)
    let lastHeight = invoiceElement.scrollHeight;
    let stableCount = 0;
    while (stableCount < 3 && Date.now() - pollStart < 35000) {
      await new Promise(r => setTimeout(r, 500));
      invoiceElement = tempContainer.querySelector('#invoice-content') as HTMLElement | null;
      if (!invoiceElement) {
        // Element disappeared — second load in progress, wait for it to reappear
        stableCount = 0;
        lastHeight = 0;
        continue;
      }
      const currentHeight = invoiceElement.scrollHeight;
      if (currentHeight === lastHeight && currentHeight > 0) {
        stableCount++;
      } else {
        stableCount = 0;
        lastHeight = currentHeight;
      }
    }

    invoiceElement = tempContainer.querySelector('#invoice-content') as HTMLElement | null;
    if (!invoiceElement) {
      console.error('generateInvoicePDF: #invoice-content not stable for', participant.name, companyNumberFilter);
      root.unmount();
      document.body.removeChild(tempContainer);
      return null;
    }

    // Numéro de la facture émise : attendre la recherche (5 s au plus).
    const billingStart = Date.now();
    while (invoiceElement && invoiceElement.dataset.billingReady !== 'true' && Date.now() - billingStart < 5000) {
      await new Promise(r => setTimeout(r, 200));
      invoiceElement = tempContainer.querySelector('#invoice-content') as HTMLElement | null;
    }
    if (!invoiceElement) {
      root.unmount();
      document.body.removeChild(tempContainer);
      return null;
    }
    await new Promise(r => setTimeout(r, 100));
    const netAmount = Number(invoiceElement.dataset.netAmount);
    const invoiceNumber = invoiceElement.dataset.invoiceNumber ?? '';

    // Override fixed positioning for capture
    const modalWrapper = tempContainer.querySelector(':scope > div') as HTMLElement | null;
    if (modalWrapper) {
      modalWrapper.style.position = 'relative';
      modalWrapper.style.inset = 'auto';
      modalWrapper.style.width = '1200px';
      modalWrapper.style.height = 'auto';
      modalWrapper.style.maxHeight = 'none';
      modalWrapper.style.overflow = 'visible';
      modalWrapper.style.background = 'white';
      modalWrapper.style.opacity = '1';
    }
    const innerWrapper = tempContainer.querySelector(':scope > div > div') as HTMLElement | null;
    if (innerWrapper) {
      innerWrapper.style.maxHeight = 'none';
      innerWrapper.style.overflow = 'visible';
      innerWrapper.style.width = '1200px';
    }

    // Make container visible for capture
    tempContainer.style.opacity = '1';
    tempContainer.style.zIndex = '-9999';

    const buttonsToHide = invoiceElement.querySelectorAll('.no-print');
    buttonsToHide.forEach((btn) => {
      (btn as HTMLElement).style.display = 'none';
    });

    await new Promise(r => setTimeout(r, 200));

    const pdf = await captureGroupedPDF(invoiceElement, undefined, true);
    const blob = pdf.output('blob');

    root.unmount();
    document.body.removeChild(tempContainer);

    if (!Number.isFinite(netAmount)) return null;
    return { blob, netAmount, invoiceNumber };
  } catch (error) {
    console.error('Error in generateInvoicePDF:', error);
    return null;
  }
}


/**
 * Montant net de la facture (négatif : décompte), calculé comme sur le PDF mais sans le générer :
 * la facture est rendue hors écran, le montant est lu puis le rendu est retiré. null si le calcul échoue.
 */
export async function computeInvoiceNetAmount(
  participant: Participant,
  selectedPeriod: { startMonth: string; endMonth: string },
  companyNumberFilter?: string | null,
): Promise<number | null> {
  const React = await import('react');
  const ReactDOM = await import('react-dom/client');
  const container = document.createElement('div');
  Object.assign(container.style, { position: 'fixed', left: '0', top: '0', width: '1200px', zIndex: '-9999', opacity: '0', pointerEvents: 'none' });
  document.body.appendChild(container);
  const root = ReactDOM.createRoot(container);
  try {
    root.render(React.createElement(InvoiceTemplate, {
      isOpen: true, onClose: () => {}, participant, selectedPeriod, companyNumberFilter,
    }));
    // Même attente que generateInvoicePDFWithMeta : contenu présent puis stable (un second chargement peut suivre).
    const start = Date.now();
    let last = '';
    let stable = 0;
    while (Date.now() - start < 35000) {
      await new Promise(r => setTimeout(r, 400));
      const el = container.querySelector('#invoice-content') as HTMLElement | null;
      const value = el?.dataset.netAmount ?? '';
      if (value && value === last) stable += 1; else stable = 0;
      last = value;
      if (stable >= 3) break;
    }
    const amount = Number(last);
    return last && Number.isFinite(amount) ? amount : null;
  } catch (error) {
    console.error('computeInvoiceNetAmount:', error);
    return null;
  } finally {
    root.unmount();
    container.remove();
  }
}

function generateMonthsInPeriod(startMonth: string, endMonth: string): string[] {
  const months: string[] = [];
  let current = startMonth;
  while (current <= endMonth) {
    months.push(current);
    const [y, m] = current.split('-').map(Number);
    const next = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
    current = next;
  }
  return months;
}